import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
} from "react";
import ReactDOM from "react-dom";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { getApiEndpoint } from "../../config/config";
import ImageCropModal from "./ImageCropModal";
import {
  Text,
  Button,
  Group,
  Stack,
  TextInput,
  Textarea,
  Select,
  MultiSelect,
  NumberInput,
  Checkbox,
  ActionIcon,
  Badge,
  Stepper,
  Modal,
} from "@mantine/core";
import {
  IconTrophy,
  IconTarget,
  IconUsers,
  IconUser,
  IconPlus,
  IconTrash,
  IconUpload,
  IconX,
  IconArrowRight,
  IconArrowLeft,
  IconDeviceFloppy,
  IconCheck,
  IconSearch,
  IconCalendarEvent,
  IconBook,
  IconSchool,
  IconFileText,
} from "@tabler/icons-react";
import PageContent from "../shared/PageContent/PageContent";
import "../Organization/OrganizationPage.css";
import "../Training/ProgramDetailsPage.css";
import "./CreateIncentiveModal.css";
import { generateBracketFromSlots } from "../../utils/bracketUtils";

// ── Sizing constants matching CSS --bk-slot-h and --bk-title-h ──────────────
const BK_SLOT_H = 28; // px — compact slot height
const BK_TITLE_H = 14; // px — compact match label height
const BK_COL_GAP = 56; // px — horizontal gap between rounds
// Height of one round-0 matchup box (title + 2 slots)
const BK_MATCHUP_H = BK_TITLE_H + BK_SLOT_H * 2; // 70px

/**
 * Connector lane width between round `roundIdx` and round `roundIdx+1`.
 *
 * The gap doubles each round to match the doubling of vertical cell height,
 * giving the classic "pyramid" bracket look where later rounds are
 * proportionally wider apart both horizontally and vertically.
 *
 * Round 0→1: 80px
 * Round 1→2: 160px
 * Round 2→3: 320px
 * Round 3→4: 640px  (capped at 640px)
 */
function getRoundConnectorGap(roundIdx) {
  if (roundIdx < 0) return 0;
  return BK_COL_GAP; // fixed gap between all rounds — no doubling
}

function getMatchupBoxMetrics(roundIdx, matchIdx) {
  const cellH = BK_MATCHUP_H * Math.pow(2, roundIdx);
  const cellTop = matchIdx * cellH;
  const matchupTopInCell = (cellH - BK_MATCHUP_H) / 2;
  const boxTop = cellTop + matchupTopInCell;
  const topSlotCenterY = boxTop + BK_TITLE_H + BK_SLOT_H / 2;
  const bottomSlotCenterY = topSlotCenterY + BK_SLOT_H;

  return {
    boxTop,
    topSlotCenterY,
    bottomSlotCenterY,
    midpointY: (topSlotCenterY + bottomSlotCenterY) / 2,
  };
}

// ── Bracket preview / editor ─────────────────────────────────────────────────
function BracketPreview({
  bracketRounds,
  bracketDesignSlots,
  bracketParticipantNameMap,
  displayedWinnersMatchNumbers,
  competitionType,
  losersBracketRounds,
  totalDisplayedWinnersMatches,
  interactive = false,
  draggedBracketSlotIndex = null,
  dragOverBracketSlotIndex = null,
  onSlotDragStart,
  onSlotDragOver,
  onSlotDragLeave,
  onSlotDrop,
  onSlotDragEnd,
}) {
  if (!bracketRounds || bracketRounds.length === 0) return null;

  const MATCHUP_W = 160; // px — width of each matchup box
  const STUB_W = 20; // px — horizontal stub from matchup right edge to vertical bar

  const getVisibleRounds = rounds => {
    const visibleRounds = [];
    rounds.forEach((round, roundIdx) => {
      const matchCount = round.matchupCount || round.matchups?.length || 0;
      if (roundIdx === 0) {
        visibleRounds.push(
          Array.from({ length: matchCount }, (_, matchIdx) => {
            const topSlot = bracketDesignSlots[matchIdx * 2];
            const bottomSlot = bracketDesignSlots[matchIdx * 2 + 1];
            return Boolean(topSlot || bottomSlot);
          })
        );
        return;
      }
      const previousVisibility = visibleRounds[roundIdx - 1] || [];
      visibleRounds.push(
        Array.from({ length: matchCount }, (_, matchIdx) =>
          Boolean(
            previousVisibility[matchIdx * 2] ||
              previousVisibility[matchIdx * 2 + 1]
          )
        )
      );
    });
    return visibleRounds;
  };

  /**
   * Build SVG connector paths for a single round column.
   *
   * The SVG coordinate space for round `roundIdx` starts at the LEFT edge of
   * that column's matchup boxes (x=0 = left edge of matchup box).
   * The SVG width = MATCHUP_W + outgoingGap.
   *
   * For each next-round match we draw:
   *   1. Horizontal stub from right edge of source A → vertical bar X
   *   2. Horizontal stub from right edge of source B → vertical bar X
   *   3. Vertical bar connecting the two stubs at x = MATCHUP_W + STUB_W
   *   4. Horizontal merge line from midpoint of vertical bar → right edge of SVG
   *      (which is the left edge of the next column's matchup box)
   *
   * For the INCOMING side (roundIdx > 0), the previous column's SVG already
   * drew the merge line ending at x = MATCHUP_W + outgoingGap of that column.
   * This column's SVG starts at x=0 = that endpoint, so we just need to draw
   * a short horizontal stub from x=0 to x=0 (nothing — the merge line already
   * arrives at the left edge of this matchup box).
   *
   * Actually: the previous column draws the merge line from the vertical bar
   * (at x = MATCHUP_W + STUB_W in that column) to x = MATCHUP_W + outgoingGap.
   * This column's left edge IS at x = MATCHUP_W + outgoingGap of the previous
   * column. So the merge line ends exactly at x=0 of this column — perfect.
   */
  // Compute left offset of each round column
  const computeRoundOffsets = rounds => {
    const offsets = [];
    let xOffset = 0;
    rounds.forEach((round, roundIdx) => {
      offsets.push(xOffset);
      const gap =
        roundIdx < rounds.length - 1 ? getRoundConnectorGap(roundIdx) : 0;
      xOffset += MATCHUP_W + gap;
    });
    return offsets;
  };

  // Returns true only if a round-0 match has both slots filled (a real matchup, not a BYE)
  const isRealMatch = (roundIdx, matchIdx) => {
    if (roundIdx !== 0) return true; // only round 0 can have BYE slots
    const topSlot = bracketDesignSlots[matchIdx * 2];
    const botSlot = bracketDesignSlots[matchIdx * 2 + 1];
    return Boolean(topSlot && botSlot);
  };

  const buildConnectors = (rounds, visibleRounds, roundOffsets) => {
    const allConnectors = [];

    rounds.forEach((round, roundIdx) => {
      if (roundIdx >= rounds.length - 1) return; // last round has no outgoing

      const matchCount = round.matchupCount || round.matchups?.length || 0;

      const colLeft = roundOffsets[roundIdx];
      const nextColLeft = roundOffsets[roundIdx + 1];
      // Vertical bar X: 20px to the right of this round's matchup right edge
      const vertBarX = colLeft + MATCHUP_W + STUB_W;

      for (let matchIdx = 0; matchIdx < matchCount; matchIdx++) {
        if (!visibleRounds[roundIdx]?.[matchIdx]) continue;

        // The next-round match that this match feeds into
        const nextMatchIdx = Math.floor(matchIdx / 2);
        if (!visibleRounds[roundIdx + 1]?.[nextMatchIdx]) continue;

        // Only draw a stub if this is a real match (both slots filled for round 0)
        const thisIsReal = isRealMatch(roundIdx, matchIdx);

        // Y center of this match's midpoint (between top and bottom slot)
        const { midpointY } = getMatchupBoxMetrics(roundIdx, matchIdx);

        // The next-round match's midpoint Y (same Y axis across all rounds)
        const nextMetrics = getMatchupBoxMetrics(roundIdx + 1, nextMatchIdx);
        const mergeY = nextMetrics.midpointY;

        // Horizontal stub: right edge of this matchup → vertical bar
        // Only draw if this is a real match (has a rendered box to connect from)
        if (thisIsReal) {
          allConnectors.push(
            <line
              key={`stub-${roundIdx}-${matchIdx}`}
              x1={colLeft + MATCHUP_W}
              y1={midpointY}
              x2={vertBarX}
              y2={midpointY}
              stroke="#d1d5db"
              strokeWidth="2"
            />
          );
        }

        // Draw vertical bar and merge line on the even (top) feeder of each pair
        if (matchIdx % 2 === 0) {
          const siblingMatchIdx = matchIdx + 1;
          const siblingVisible = visibleRounds[roundIdx]?.[siblingMatchIdx];
          const siblingIsReal = isRealMatch(roundIdx, siblingMatchIdx);
          const siblingMetrics = siblingVisible
            ? getMatchupBoxMetrics(roundIdx, siblingMatchIdx)
            : null;

          const topY = midpointY;
          const bottomY = siblingMetrics ? siblingMetrics.midpointY : midpointY;

          // Vertical bar: draw if BOTH feeders are real (full bracket shape),
          // OR if only one feeder is real and its stub Y differs from mergeY
          // (need a vertical connector from the stub to the merge line)
          const eitherIsReal = thisIsReal || (siblingVisible && siblingIsReal);
          if (siblingVisible && thisIsReal && siblingIsReal) {
            // Both real: vertical bar spans from top stub to bottom stub
            allConnectors.push(
              <line
                key={`vbar-${roundIdx}-${matchIdx}`}
                x1={vertBarX}
                y1={topY}
                x2={vertBarX}
                y2={bottomY}
                stroke="#d1d5db"
                strokeWidth="2"
              />
            );
          } else if (eitherIsReal && roundIdx === 0) {
            // Only one feeder is real — draw a vertical connector from that
            // stub's Y to the merge line Y so the lines connect
            const realY = thisIsReal
              ? midpointY
              : siblingMetrics
                ? siblingMetrics.midpointY
                : mergeY;
            if (realY !== mergeY) {
              allConnectors.push(
                <line
                  key={`vbar-${roundIdx}-${matchIdx}`}
                  x1={vertBarX}
                  y1={realY}
                  x2={vertBarX}
                  y2={mergeY}
                  stroke="#d1d5db"
                  strokeWidth="2"
                />
              );
            }
          }

          // Merge line: always connects to next-round match midpointY (mergeY)
          // Draw if at least one feeder is real (or if this is not round 0)
          if (roundIdx !== 0 || eitherIsReal) {
            allConnectors.push(
              <line
                key={`merge-${roundIdx}-${matchIdx}`}
                x1={vertBarX}
                y1={mergeY}
                x2={nextColLeft}
                y2={mergeY}
                stroke="#d1d5db"
                strokeWidth="2"
              />
            );
          }
        }
      }
    });

    return allConnectors;
  };

  const renderRounds = (rounds, isWinners = true) => {
    const r0MatchCount =
      rounds[0]?.matchupCount || rounds[0]?.matchups?.length || 0;
    const totalH = r0MatchCount * BK_MATCHUP_H;
    const visibleRounds = getVisibleRounds(rounds);

    // Compute left offset of each round column (for absolute positioning of matchup boxes)
    const roundOffsets = computeRoundOffsets(rounds);

    // Compute total SVG width (spans all columns + gaps)
    const svgTotalW = roundOffsets[roundOffsets.length - 1] + MATCHUP_W;

    // Build all connectors in a single SVG overlay
    const connectorElements = buildConnectors(
      rounds,
      visibleRounds,
      roundOffsets
    );

    const matchupBoxes = rounds.flatMap((round, roundIdx) => {
      const matchCount = round.matchupCount || round.matchups?.length || 0;
      const colLeft = roundOffsets[roundIdx];

      return Array.from({ length: matchCount }, (_, matchIdx) => {
        if (!visibleRounds[roundIdx]?.[matchIdx]) return null;

        const { boxTop } = getMatchupBoxMetrics(roundIdx, matchIdx);
        const mapKey = isWinners ? `${roundIdx}-${matchIdx}` : null;
        const matchupNumber = mapKey
          ? displayedWinnersMatchNumbers.get(mapKey)
          : null;
        const matchupTitle = matchupNumber ? `Match ${matchupNumber}` : "";

        if (round.isFirstRound) {
          const topIdx = matchIdx * 2;
          const botIdx = matchIdx * 2 + 1;
          const topSlot = bracketDesignSlots[topIdx];
          const botSlot = bracketDesignSlots[botIdx];

          // Skip BYE matchups entirely — don't render if either slot is empty
          if (!topSlot || !botSlot) return null;

          const slotHandlers = (slotIndex, slotValue) => ({
            draggable: interactive && Boolean(slotValue),
            onDragStart:
              interactive && slotValue
                ? () => onSlotDragStart?.(slotIndex)
                : undefined,
            onDragOver: interactive
              ? e => {
                  e.preventDefault();
                  onSlotDragOver?.(slotIndex);
                }
              : undefined,
            onDragLeave: interactive ? () => onSlotDragLeave?.() : undefined,
            onDrop: interactive
              ? e => {
                  e.preventDefault();
                  onSlotDrop?.(slotIndex);
                }
              : undefined,
            onDragEnd: interactive ? () => onSlotDragEnd?.() : undefined,
          });

          return (
            <div
              key={`${roundIdx}-${matchIdx}`}
              style={{
                position: "absolute",
                top: boxTop,
                left: colLeft,
                width: MATCHUP_W,
              }}
            >
              <div className="bracket-matchup">
                <div className="bracket-matchup-title">{matchupTitle}</div>
                <div
                  className={`bracket-slot ${topSlot ? "bracket-slot--filled" : "bracket-slot--bye"} ${dragOverBracketSlotIndex === topIdx ? "bracket-slot--drag-over" : ""} ${draggedBracketSlotIndex === topIdx ? "bracket-slot--dragging" : ""}`}
                  {...slotHandlers(topIdx, topSlot)}
                >
                  <span className="bracket-seed">#{topIdx + 1}</span>
                  <span className="bracket-name">{topSlot?.name || "BYE"}</span>
                </div>
                <div
                  className={`bracket-slot ${botSlot ? "bracket-slot--filled" : "bracket-slot--bye"} ${dragOverBracketSlotIndex === botIdx ? "bracket-slot--drag-over" : ""} ${draggedBracketSlotIndex === botIdx ? "bracket-slot--dragging" : ""}`}
                  {...slotHandlers(botIdx, botSlot)}
                >
                  <span className="bracket-seed">#{botIdx + 1}</span>
                  <span className="bracket-name">{botSlot?.name || "BYE"}</span>
                </div>
              </div>
            </div>
          );
        }

        const prevRound = rounds[roundIdx - 1];
        const sourceA = prevRound?.matchups?.[matchIdx * 2] || null;
        const sourceB = prevRound?.matchups?.[matchIdx * 2 + 1] || null;

        const getSourceDisplay = (source, sourceIndex) => {
          const srcMapKey = isWinners ? `${roundIdx - 1}-${sourceIndex}` : null;
          const srcMatchNumber = srcMapKey
            ? displayedWinnersMatchNumbers.get(srcMapKey)
            : null;
          const srcLabel = srcMatchNumber
            ? `Winner of Match ${srcMatchNumber}`
            : "Winner of previous match";
          if (!source?.winner) return { label: srcLabel, known: false };
          const winnerName = bracketParticipantNameMap.get(
            String(source.winner)
          );
          return winnerName
            ? { label: winnerName, known: true }
            : { label: srcLabel, known: false };
        };

        const sourceADisplay = getSourceDisplay(sourceA, matchIdx * 2);
        const sourceBDisplay = getSourceDisplay(sourceB, matchIdx * 2 + 1);
        const hasSourceA = isWinners
          ? displayedWinnersMatchNumbers.has(`${roundIdx - 1}-${matchIdx * 2}`)
          : true;
        const hasSourceB = isWinners
          ? displayedWinnersMatchNumbers.has(
              `${roundIdx - 1}-${matchIdx * 2 + 1}`
            )
          : true;

        return (
          <div
            key={`${roundIdx}-${matchIdx}`}
            style={{
              position: "absolute",
              top: boxTop,
              left: colLeft,
              width: MATCHUP_W,
            }}
          >
            <div className="bracket-matchup bracket-matchup--tbd">
              <div className="bracket-matchup-title">{matchupTitle}</div>
              <div
                className={`bracket-slot ${sourceADisplay.known ? "bracket-slot--filled" : "bracket-slot--tbd"} ${hasSourceA ? "bracket-slot--has-source" : ""}`}
              >
                <span
                  className={`bracket-name ${sourceADisplay.known ? "" : "bracket-name--source"}`}
                >
                  {sourceADisplay.label}
                </span>
              </div>
              <div
                className={`bracket-slot ${sourceBDisplay.known ? "bracket-slot--filled" : "bracket-slot--tbd"} ${hasSourceB ? "bracket-slot--has-source" : ""}`}
              >
                <span
                  className={`bracket-name ${sourceBDisplay.known ? "" : "bracket-name--source"}`}
                >
                  {sourceBDisplay.label}
                </span>
              </div>
            </div>
          </div>
        );
      });
    });

    return (
      <div
        style={{
          position: "relative",
          height: totalH,
          width: svgTotalW,
          flexShrink: 0,
        }}
      >
        {/* Single SVG overlay for all connectors */}
        <svg
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: svgTotalW,
            height: totalH,
            overflow: "visible",
            pointerEvents: "none",
          }}
          viewBox={`0 0 ${svgTotalW} ${totalH}`}
          preserveAspectRatio="xMinYMin meet"
        >
          {connectorElements}
        </svg>
        {matchupBoxes}
      </div>
    );
  };

  // Total height for champion column
  const r0MatchCount =
    bracketRounds[0]?.matchupCount || bracketRounds[0]?.matchups?.length || 0;
  const totalH = r0MatchCount * BK_MATCHUP_H;
  // Champion Y = midpoint of the entire bracket (midpoint of the final match)
  const championMidY = getMatchupBoxMetrics(
    bracketRounds.length - 1,
    0
  ).midpointY;
  const championIncomingGap = getRoundConnectorGap(bracketRounds.length - 1);

  return (
    <>
      <div
        className="tournament-bracket"
        style={{ alignItems: "flex-start", paddingTop: 0 }}
      >
        {renderRounds(bracketRounds, true)}
        {/* Champion column */}
        <div
          style={{
            position: "relative",
            height: totalH,
            width: championIncomingGap + 130,
            flexShrink: 0,
          }}
        >
          <div
            className="bracket-round-label"
            style={{
              position: "absolute",
              top: -24,
              left: 0,
              width: 130,
              textAlign: "center",
              marginLeft: championIncomingGap,
            }}
          >
            Champion
          </div>
          {/* Horizontal line from last round's merge point to champion box */}
          <svg
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: championIncomingGap,
              height: totalH,
              overflow: "visible",
              pointerEvents: "none",
            }}
          >
            <line
              x1={0}
              y1={championMidY}
              x2={championIncomingGap}
              y2={championMidY}
              stroke="#d1d5db"
              strokeWidth="2"
            />
          </svg>
          <div
            style={{
              position: "absolute",
              top: championMidY - 40,
              left: championIncomingGap,
              width: 130,
            }}
          >
            <div className="bracket-champion">
              <span className="bracket-champion-icon">🏆</span>
              <span className="bracket-champion-label">Champion</span>
            </div>
          </div>
        </div>
      </div>

      {competitionType === "double_elimination" &&
        losersBracketRounds.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <Text size="xs" fw={600} c="dimmed" mb={6}>
              Losers Bracket
            </Text>
            <div
              className="tournament-bracket"
              style={{ alignItems: "flex-start", paddingTop: 32 }}
            >
              {losersBracketRounds.map((round, roundIdx) => {
                const matchCount = round.matchups.length;
                const lTotalH =
                  matchCount * BK_MATCHUP_H * Math.pow(2, roundIdx);
                const priorLosersMatchups = losersBracketRounds
                  .slice(0, roundIdx)
                  .reduce((total, r) => total + r.matchups.length, 0);
                const offset =
                  totalDisplayedWinnersMatches + priorLosersMatchups;
                const cellH = BK_MATCHUP_H * Math.pow(2, roundIdx);
                const incomingGap =
                  roundIdx > 0 ? getRoundConnectorGap(roundIdx - 1) : 0;
                const outgoingGap =
                  roundIdx < losersBracketRounds.length - 1
                    ? getRoundConnectorGap(roundIdx)
                    : 0;
                const colWidth = incomingGap + 160 + outgoingGap;

                return (
                  <div
                    key={roundIdx}
                    className="bracket-round"
                    style={{
                      position: "relative",
                      height: lTotalH,
                      minWidth: colWidth,
                      flexShrink: 0,
                    }}
                  >
                    <div
                      className="bracket-round-label"
                      style={{
                        position: "absolute",
                        top: -24,
                        left: 0,
                        right: 0,
                      }}
                    >
                      {round.name}
                    </div>
                    {round.matchups.map((matchup, matchIdx) => {
                      const matchupTopInCell = (cellH - BK_MATCHUP_H) / 2;
                      const boxTop = matchIdx * cellH + matchupTopInCell;
                      return (
                        <div
                          key={matchIdx}
                          style={{
                            position: "absolute",
                            top: boxTop,
                            left: incomingGap,
                            right: 0,
                          }}
                        >
                          <div className="bracket-matchup bracket-matchup--tbd">
                            <div className="bracket-matchup-title">
                              Match {offset + matchIdx + 1}
                            </div>
                            <div className="bracket-slot bracket-slot--tbd">
                              <span className="bracket-name bracket-name--source">
                                {matchup.source === "drop_from_winners"
                                  ? "Drop from Winners"
                                  : "Loser of prev. round"}
                              </span>
                            </div>
                            <div className="bracket-slot bracket-slot--tbd">
                              <span className="bracket-name bracket-name--source">
                                {matchup.source === "drop_from_winners"
                                  ? "Drop from Winners"
                                  : "Loser of prev. round"}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}
    </>
  );
}

const BRACKET_FORMATS = new Set([
  "single_elimination",
  "double_elimination",
  "king_of_the_hill",
]);

const nextPow2 = n => {
  let p = 1;
  while (p < n) p <<= 1;
  return p;
};

const buildFoldedSeedOrder = size => {
  let seeds = [1];
  while (seeds.length < size) {
    const nextSize = seeds.length * 2;
    const next = [];
    for (const s of seeds) {
      next.push(s);
      next.push(nextSize + 1 - s);
    }
    seeds = next;
  }
  return seeds;
};

const getSeedSlotPriority = size => {
  const folded = buildFoldedSeedOrder(size); // slot -> seed
  const seedToSlot = new Array(size);
  folded.forEach((seed, slotIndex) => {
    seedToSlot[seed - 1] = slotIndex;
  });
  return seedToSlot; // seed-ordered slot preference (1,2,3...)
};

const syncBracketSlots = (prevSlots, participants, competitionType) => {
  if (!participants || participants.length < 2) return [];
  const participantMap = new Map(
    participants.map(p => [String(p.id), { id: p.id, name: p.name }])
  );

  const targetSize =
    competitionType === "king_of_the_hill"
      ? participants.length
      : nextPow2(Math.max(2, participants.length));

  const nextSlots = new Array(targetSize).fill(null);
  const usedIds = new Set();

  for (let i = 0; i < Math.min(prevSlots.length, targetSize); i++) {
    const slot = prevSlots[i];
    if (!slot) continue;
    const existing = participantMap.get(String(slot.id));
    if (existing && !usedIds.has(String(existing.id))) {
      nextSlots[i] = existing;
      usedIds.add(String(existing.id));
    }
  }

  const remaining = participants.filter(p => !usedIds.has(String(p.id)));

  const slotPriority =
    competitionType === "king_of_the_hill"
      ? Array.from({ length: targetSize }, (_, i) => i)
      : getSeedSlotPriority(targetSize);

  for (const participant of remaining) {
    const targetIndex = slotPriority.find(index => !nextSlots[index]);
    if (targetIndex == null) break;
    nextSlots[targetIndex] = { id: participant.id, name: participant.name };
  }

  return nextSlots;
};

const METRIC_OPTIONS = [
  { value: "training_minutes", label: "Training Minutes" },
  { value: "days_completed", label: "Days Completed" },
  { value: "revenue", label: "Revenue" },
  { value: "leads_created", label: "Leads Created" },
  { value: "meetings_held", label: "Meetings Held" },
  { value: "tasks_completed", label: "Tasks Completed" },
  { value: "videos_watched", label: "Videos Watched" },
];

const WEEKDAY_OPTIONS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const DAYS_COMPLETED_BASE_METRIC_OPTIONS = [
  { value: "training_minutes", label: "Training Minutes" },
];

const ADMIN_SECTIONS = [
  {
    id: "courses",
    label: "Training Courses",
    icon: IconBook,
    path: "/admin?tab=courses",
  },
  {
    id: "incentives",
    label: "Incentives",
    icon: IconTrophy,
    path: "/admin?tab=incentives",
  },
  {
    id: "programs",
    label: "Programs",
    icon: IconSchool,
    path: "/admin?tab=programs",
  },
  {
    id: "incentive-creation",
    label: "Create Incentive",
    icon: IconPlus,
    path: null,
  },
  {
    id: "groups",
    label: "Custom Groups",
    icon: IconUsers,
    path: "/admin?tab=groups",
  },
  {
    id: "documents",
    label: "Documents",
    icon: IconFileText,
    path: "/admin?tab=documents",
  },
];

function IncentiveFormPage() {
  const { getVerifiedJWT } = useAuth();
  const navigate = useNavigate();
  const { id: incentiveId } = useParams();
  const isEditMode = Boolean(incentiveId);
  // eslint-disable-next-line no-unused-vars
  const [loadingIncentive, setLoadingIncentive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const bannerInputRef = useRef(null);
  const prizeImageInputRef = useRef(null);
  const [activePrizeImageIndex, setActivePrizeImageIndex] = useState(null);

  // Basics
  const [title, setTitle] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [bannerPosition, setBannerPosition] = useState({ x: 50, y: 50 });
  const [type, setType] = useState("");
  const [competitionLevel, setCompetitionLevel] = useState("individual");
  const [competitionType, setCompetitionType] = useState("");
  const [bracketParticipants, setBracketParticipants] = useState([]);
  const [bracketAddAll, setBracketAddAll] = useState(false);
  const [bracketDesignSlots, setBracketDesignSlots] = useState([]);
  const [draggedBracketSlotIndex, setDraggedBracketSlotIndex] = useState(null);
  const [dragOverBracketSlotIndex, setDragOverBracketSlotIndex] =
    useState(null);
  const [bracketParticipantSearch, setBracketParticipantSearch] = useState("");
  const [bracketCompetitorMode, setBracketCompetitorMode] = useState("rep");
  const [bracketAddedGroups, setBracketAddedGroups] = useState([]); // { id, name } groups added via group search
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Match Ups
  const [matchups, setMatchups] = useState([
    { id: 1, sideA: null, sideB: null },
  ]);
  const [matchupRepSearch, setMatchupRepSearch] = useState(null);
  const matchupBlurTimerRef = useRef(null);
  const repSelectorRefs = useRef({});
  const [dropdownCoords, setDropdownCoords] = useState(null);

  useLayoutEffect(() => {
    if (!matchupRepSearch) {
      setDropdownCoords(null);
      return;
    }
    const key = `${matchupRepSearch.matchupId}-${matchupRepSearch.side}`;
    const el = repSelectorRefs.current[key];
    if (el) {
      const rect = el.getBoundingClientRect();
      setDropdownCoords({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [matchupRepSearch]);

  // Scoring & Prizes
  const [metricType, setMetricType] = useState("training_minutes");
  const [daysCompletedMetricType, setDaysCompletedMetricType] =
    useState("training_minutes");
  const [daysCompletedThresholdValue, setDaysCompletedThresholdValue] =
    useState("");
  const [daysCompletedExcludedWeekdays, setDaysCompletedExcludedWeekdays] =
    useState([]);
  const [prizes, setPrizes] = useState([
    {
      _id: Date.now(),
      title: "",
      imagePreview: null,
      imagePosition: { x: 50, y: 50 },
      benchmark_value: "",
      image_url: "",
      prize_link: "",
      first_to_limit_enabled: false,
      first_to_limit: "",
    },
  ]);

  // Crop modal
  const [cropModal, setCropModal] = useState(null);

  // Assignment & Activation

  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [additionalRepIds, setAdditionalRepIds] = useState([]);
  // const [repSearchTerm, setRepSearchTerm] = useState("");
  const [allDownline, setAllDownline] = useState([]);
  const [groups, setGroups] = useState([]);
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});
  const [summaryModal, setSummaryModal] = useState(null); // "metric" | "tiebreaker" | "conditions" | "startDate" | "endDate" | null
  const [tiebreaker, setTiebreaker] = useState("");
  const [conditions, setConditions] = useState("");

  // Fetch groups and downline on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const jwt = await getVerifiedJWT();
        const [repsRes, groupsRes] = await Promise.all([
          fetch(getApiEndpoint("/v1/company-downline/reps-by-manager"), {
            headers: { Authorization: `Bearer ${jwt}` },
          }),
          fetch(getApiEndpoint("/v1/groups/owned"), {
            headers: { Authorization: `Bearer ${jwt}` },
          }),
        ]);
        if (repsRes.ok) {
          const data = await repsRes.json();
          setAllDownline(data.data?.reps || []);
        }
        if (groupsRes.ok) {
          const data = await groupsRes.json();
          setGroups(data.data || data || []);
        }
      } catch {
        /* silent */
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolveBracketParticipantName = useCallback(
    (id, level = competitionLevel) => {
      if (level === "team") {
        const group = (groups || []).find(g => String(g.id) === String(id));
        if (group?.name) return group.name;
      } else {
        const rep = allDownline.find(
          r => String(r.user_season_id || r.id) === String(id)
        );
        if (rep) {
          const name = `${rep.first_name || ""} ${rep.last_name || ""}`.trim();
          if (name) return name;
          if (rep.email) return rep.email;
        }
      }
      return `#${id}`;
    },
    [allDownline, groups, competitionLevel]
  );

  // In edit mode, fetch the existing incentive and pre-populate state
  useEffect(() => {
    if (!isEditMode) return;
    const fetchIncentive = async () => {
      setLoadingIncentive(true);
      try {
        const jwt = await getVerifiedJWT();
        const res = await fetch(
          getApiEndpoint(`/v1/incentives/${incentiveId}`),
          { headers: { Authorization: `Bearer ${jwt}` } }
        );
        if (!res.ok) return;
        const json = await res.json();
        const inc = json.data || json;
        // Pre-populate basics
        if (inc.title) setTitle(inc.title);
        if (inc.description) setDescription(inc.description);
        if (inc.type) setType(inc.type);
        if (inc.competition_level) setCompetitionLevel(inc.competition_level);
        if (inc.competition_type) setCompetitionType(inc.competition_type);
        if (
          inc.bracket_participant_ids &&
          inc.bracket_participant_ids.length > 0
        ) {
          if (BRACKET_FORMATS.has(inc.competition_type || "")) {
            setBracketAddAll(true);
          }
          setBracketParticipants(
            inc.bracket_participant_ids.map(id => ({
              id,
              name: resolveBracketParticipantName(
                id,
                inc.competition_level || competitionLevel
              ),
            }))
          );
        }
        if (inc.metric_type) setMetricType(inc.metric_type);
        if (inc.days_completed_metric_type)
          setDaysCompletedMetricType(inc.days_completed_metric_type);
        if (inc.days_completed_threshold_value != null)
          setDaysCompletedThresholdValue(
            String(inc.days_completed_threshold_value)
          );
        if (Array.isArray(inc.days_completed_excluded_weekdays))
          setDaysCompletedExcludedWeekdays(
            inc.days_completed_excluded_weekdays
          );
        if (inc.start_date) setStartDate(new Date(inc.start_date));
        if (inc.end_date) setEndDate(new Date(inc.end_date));
        if (inc.banner_url) setBannerPreview(inc.banner_url);
        if (inc.tiebreaker) setTiebreaker(inc.tiebreaker);
        if (inc.conditions) setConditions(inc.conditions);
        // Pre-populate prizes
        if (inc.prizes && inc.prizes.length > 0) {
          setPrizes(
            inc.prizes.map((p, i) => ({
              _id: Date.now() + i,
              title: p.title || "",
              imagePreview: p.image_url || null,
              imagePosition: { x: 50, y: 50 },
              benchmark_value: p.benchmark_value ?? "",
              image_url: p.image_url || "",
              prize_link: p.prize_link || "",
              first_to_limit_enabled:
                p.first_to_limit != null && p.first_to_limit > 0,
              first_to_limit:
                p.first_to_limit != null ? String(p.first_to_limit) : "",
            }))
          );
        }
        // Pre-populate groups
        if (inc.groups && inc.groups.length > 0) {
          setSelectedGroupIds(inc.groups.map(g => g.id || g));
        } else if (inc.group_ids && inc.group_ids.length > 0) {
          setSelectedGroupIds(inc.group_ids);
        }
        // Pre-populate additional reps
        if (inc.additional_rep_ids && inc.additional_rep_ids.length > 0) {
          setAdditionalRepIds(inc.additional_rep_ids);
        }
        // Pre-populate matchups for competition type
        if (
          inc.type === "competition" &&
          inc.matchups &&
          inc.matchups.length > 0
        ) {
          setMatchups(
            inc.matchups.map((m, i) => ({
              id: i + 1,
              sideA: m.sideA || m.side_a || null,
              sideB: m.sideB || m.side_b || null,
            }))
          );
        }
      } catch {
        /* silent */
      } finally {
        setLoadingIncentive(false);
      }
    };
    fetchIncentive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    incentiveId,
    isEditMode,
    resolveBracketParticipantName,
    competitionLevel,
  ]);

  useEffect(() => {
    if (!bracketParticipants.length) return;

    setBracketParticipants(prev => {
      let changed = false;
      const next = prev.map(p => {
        if (p.name && !String(p.name).startsWith("#")) return p;
        const resolved = resolveBracketParticipantName(p.id, competitionLevel);
        if (resolved !== p.name) {
          changed = true;
          return { ...p, name: resolved };
        }
        return p;
      });
      return changed ? next : prev;
    });
  }, [
    allDownline,
    groups,
    competitionLevel,
    resolveBracketParticipantName,
    bracketParticipants.length,
  ]);

  // const groupRepsMap = useMemo(() => {
  //   if (!groups || selectedGroupIds.length === 0) return {};
  //   const map = {};
  //   groups
  //     .filter(g => selectedGroupIds.includes(g.id))
  //     .forEach(group => {
  //       const members = [];
  //       (group.group_users || []).forEach(gu => {
  //         const us = gu.user_season || gu;
  //         const user = us.user || us;
  //         const id = us.user_season_id || us.id || gu.user_season_id;
  //         if (id)
  //           members.push({
  //             user_season_id: id,
  //             first_name: user.first_name || us.first_name || "",
  //             last_name: user.last_name || us.last_name || "",
  //             email: user.email || us.email || "",
  //           });
  //       });
  //       map[group.id] = { name: group.name, members };
  //     });
  //   return map;
  // }, [groups, selectedGroupIds]);

  // const groupReps = useMemo(() => {
  //   const repMap = new Map();
  //   Object.values(groupRepsMap).forEach(({ members }) => {
  //     members.forEach(m => {
  //       if (!repMap.has(m.user_season_id)) repMap.set(m.user_season_id, m);
  //     });
  //   });
  //   return Array.from(repMap.values());
  // }, [groupRepsMap]);

  // const totalGroupRepCount = groupReps.length;

  // const additionalReps = useMemo(() => {
  //   const groupRepIds = new Set(groupReps.map(r => r.user_season_id));
  //   return allDownline.filter(
  //     r =>
  //       additionalRepIds.includes(r.user_season_id || r.id) &&
  //       !groupRepIds.has(r.user_season_id || r.id)
  //   );
  // }, [allDownline, additionalRepIds, groupReps]);

  const extractGroupMembers = useCallback(group => {
    const members = [];
    (group?.group_users || []).forEach(gu => {
      const us = gu.user_season || gu;
      const user = us.user || us;
      const id = us.user_season_id || us.id || gu.user_season_id;
      if (id) {
        members.push({
          user_season_id: id,
          first_name: user.first_name || us.first_name || "",
          last_name: user.last_name || us.last_name || "",
          email: user.email || us.email || "",
        });
      }
    });
    return members;
  }, []);

  // const searchResults = useMemo(() => {
  //   if (!repSearchTerm.trim()) return [];
  //   const groupRepIds = new Set(groupReps.map(r => r.user_season_id));
  //   const addedIds = new Set(additionalRepIds);
  //   const term = repSearchTerm.toLowerCase();
  //   return allDownline
  //     .filter(r => {
  //       const id = r.user_season_id || r.id;
  //       if (groupRepIds.has(id) || addedIds.has(id)) return false;
  //       const name = `${r.first_name || ""} ${r.last_name || ""}`.toLowerCase();
  //       const email = (r.email || "").toLowerCase();
  //       return name.includes(term) || email.includes(term);
  //     })
  //     .slice(0, 8);
  // }, [repSearchTerm, allDownline, groupReps, additionalRepIds]);

  const bracketCompetitorSearchResults = useMemo(() => {
    if (!bracketParticipantSearch.trim()) return [];
    const term = bracketParticipantSearch.toLowerCase();

    if (bracketCompetitorMode === "rep") {
      const selectedIds = new Set(bracketParticipants.map(p => String(p.id)));
      return allDownline
        .filter(r => {
          const id = String(r.user_season_id || r.id);
          if (selectedIds.has(id)) return false;
          const name =
            `${r.first_name || ""} ${r.last_name || ""}`.toLowerCase();
          const email = (r.email || "").toLowerCase();
          return name.includes(term) || email.includes(term);
        })
        .slice(0, 8)
        .map(r => ({
          type: "rep",
          id: r.user_season_id || r.id,
          label: `${r.first_name || ""} ${r.last_name || ""}`.trim(),
        }));
    }

    return (groups || [])
      .filter(g => g.name?.toLowerCase().includes(term))
      .slice(0, 8)
      .map(g => ({ type: bracketCompetitorMode, id: g.id, label: g.name }));
  }, [
    bracketParticipantSearch,
    bracketCompetitorMode,
    allDownline,
    groups,
    bracketParticipants,
  ]);

  // const addRep = repId => {
  //   setAdditionalRepIds(prev => [...prev, repId]);
  //   setRepSearchTerm("");
  // };

  const addBracketRep = repId => {
    const rep = allDownline.find(
      r => String(r.user_season_id || r.id) === String(repId)
    );
    if (!rep) return;
    setBracketParticipants(prev => {
      if (prev.some(p => String(p.id) === String(repId))) return prev;
      return [
        ...prev,
        {
          id: rep.user_season_id || rep.id,
          name: `${rep.first_name || ""} ${rep.last_name || ""}`.trim(),
        },
      ];
    });
    setBracketParticipantSearch("");
  };

  const addBracketGroupOrTeam = (groupId, trackGroup = false) => {
    const group = (groups || []).find(g => String(g.id) === String(groupId));
    if (!group) return;
    const members = extractGroupMembers(group);
    const addedMemberIds = [];
    setBracketParticipants(prev => {
      const seen = new Set(prev.map(p => String(p.id)));
      const next = [...prev];
      members.forEach(m => {
        const id = String(m.user_season_id);
        if (seen.has(id)) return;
        seen.add(id);
        addedMemberIds.push(id);
        next.push({
          id: m.user_season_id,
          name: `${m.first_name || ""} ${m.last_name || ""}`.trim(),
        });
      });
      return next;
    });
    if (trackGroup) {
      setBracketAddedGroups(prev => {
        if (prev.some(g => String(g.id) === String(groupId))) return prev;
        // Store member IDs so we can display rep chips even if group_users isn't populated
        const memberIds = members.map(m => String(m.user_season_id));
        return [...prev, { id: group.id, name: group.name, memberIds }];
      });
    }
    setBracketParticipantSearch("");
  };

  const removeGroupFromBracket = groupId => {
    const group = (groups || []).find(g => String(g.id) === String(groupId));
    if (!group) return;
    const memberIds = new Set(
      extractGroupMembers(group).map(m => String(m.user_season_id))
    );
    setBracketParticipants(prev =>
      prev.filter(p => !memberIds.has(String(p.id)))
    );
    setBracketAddedGroups(prev =>
      prev.filter(g => String(g.id) !== String(groupId))
    );
  };

  const getBracketGroupRepChips = groupId => {
    const group = (groups || []).find(g => String(g.id) === String(groupId));
    const directMembers = group ? extractGroupMembers(group) : [];

    if (directMembers.length > 0) {
      return directMembers.map(member => ({
        id: member.user_season_id,
        name: `${member.first_name || ""} ${member.last_name || ""}`.trim(),
      }));
    }

    const addedGroup = bracketAddedGroups.find(
      g => String(g.id) === String(groupId)
    );
    const memberIds = new Set((addedGroup?.memberIds || []).map(String));
    return bracketParticipants.filter(p => memberIds.has(String(p.id)));
  };

  // const removeAdditionalRep = repId =>
  //   setAdditionalRepIds(prev => prev.filter(id => id !== repId));

  const addMatchup = () =>
    setMatchups(prev => [
      ...prev,
      { id: Date.now(), sideA: null, sideB: null },
    ]);
  const removeMatchup = id =>
    setMatchups(prev => prev.filter(m => m.id !== id));
  const updateMatchupSide = (id, side, value) =>
    setMatchups(prev =>
      prev.map(m => (m.id === id ? { ...m, [side]: value } : m))
    );

  const matchupRepSearchResults = useMemo(() => {
    if (!matchupRepSearch?.term?.trim()) return [];
    const term = matchupRepSearch.term.toLowerCase();
    const currentMatchupId = matchupRepSearch.matchupId;
    const currentSide = matchupRepSearch.side;
    const usedIds = new Set(
      matchups.flatMap(m => {
        const ids = [];
        if (m.id === currentMatchupId) {
          const otherSide = currentSide === "sideA" ? m.sideB : m.sideA;
          if (otherSide) ids.push(otherSide);
        } else {
          if (m.sideA) ids.push(m.sideA);
          if (m.sideB) ids.push(m.sideB);
        }
        return ids;
      })
    );
    return allDownline
      .filter(r => {
        const id = r.user_season_id || r.id;
        if (usedIds.has(id)) return false;
        const name = `${r.first_name || ""} ${r.last_name || ""}`.toLowerCase();
        const email = (r.email || "").toLowerCase();
        return name.includes(term) || email.includes(term);
      })
      .slice(0, 6);
  }, [matchupRepSearch, allDownline, matchups]);

  const openMatchupSearch = (matchupId, side) => {
    if (matchupBlurTimerRef.current) clearTimeout(matchupBlurTimerRef.current);
    setMatchupRepSearch(prev =>
      prev?.matchupId === matchupId && prev?.side === side
        ? prev
        : { matchupId, side, term: "" }
    );
  };
  const closeMatchupSearch = () => {
    matchupBlurTimerRef.current = setTimeout(
      () => setMatchupRepSearch(null),
      200
    );
  };
  const selectMatchupRep = (matchupId, side, repId) => {
    if (matchupBlurTimerRef.current) clearTimeout(matchupBlurTimerRef.current);
    updateMatchupSide(matchupId, side, repId);
    setMatchupRepSearch(null);
  };

  const handlePrizeImageSelect = useCallback(
    e => {
      const file = e.target.files[0];
      if (file && activePrizeImageIndex !== null) {
        const idx = activePrizeImageIndex;
        const reader = new FileReader();
        reader.onloadend = () => {
          const src = reader.result;
          setCropModal({
            src,
            aspectRatio: "prize",
            initialPos: prizes[idx]?.imagePosition || { x: 50, y: 50 },
            onConfirm: pos => {
              updatePrize(idx, "imagePreview", src);
              updatePrize(idx, "imagePosition", pos);
              setCropModal(null);
            },
          });
        };
        reader.readAsDataURL(file);
        e.target.value = "";
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [activePrizeImageIndex, prizes]
  );

  const handleBannerSelect = e => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        const src = reader.result;
        setCropModal({
          src,
          aspectRatio: "banner",
          initialPos: bannerPosition,
          onConfirm: pos => {
            setBannerPreview(src);
            setBannerPosition(pos);
            setCropModal(null);
          },
        });
      };
      reader.readAsDataURL(file);
      e.target.value = "";
    }
  };

  const removeBanner = () => {
    setBannerFile(null);
    setBannerPreview(null);
    setBannerPosition({ x: 50, y: 50 });
    if (bannerInputRef.current) bannerInputRef.current.value = "";
  };

  // const toggleGroup = groupId =>
  //   setSelectedGroupIds(prev =>
  //     prev.includes(groupId)
  //       ? prev.filter(id => id !== groupId)
  //       : [...prev, groupId]
  //   );

  const sortPrizesByBenchmark = prizes => {
    return [...prizes].sort((a, b) => {
      const aVal = parseFloat(a.benchmark_value);
      const bVal = parseFloat(b.benchmark_value);
      const aIsNum = !isNaN(aVal);
      const bIsNum = !isNaN(bVal);
      // Tiers with no value go to the end
      if (!aIsNum && !bIsNum) return 0;
      if (!aIsNum) return 1;
      if (!bIsNum) return -1;
      return aVal - bVal;
    });
  };

  const addPrize = () =>
    setPrizes(prev =>
      sortPrizesByBenchmark([
        ...prev,
        {
          _id: Date.now() + Math.random(),
          title: "",
          imagePreview: null,
          imagePosition: { x: 50, y: 50 },
          benchmark_value: "",
          image_url: "",
          prize_link: "",
          first_to_limit_enabled: false,
          first_to_limit: "",
        },
      ])
    );
  const removePrize = index =>
    setPrizes(prev => prev.filter((_, i) => i !== index));
  const updatePrize = (index, field, value) =>
    setPrizes(prev =>
      prev.map((prize, i) =>
        i === index ? { ...prize, [field]: value } : prize
      )
    );
  // Sort milestone tiers by benchmark_value when user leaves the target value field
  const handleBenchmarkBlur = () => {
    setPrizes(prev => sortPrizesByBenchmark(prev));
  };

  const buildPayload = isDraft => {
    const orderedBracketParticipants =
      bracketDesignSlots.filter(Boolean).length > 0
        ? bracketDesignSlots.filter(Boolean)
        : bracketParticipants;
    const payload = {
      title: title.trim(),
      description: description.trim() || undefined,
      type: type || "milestone",
      competition_level: type === "competition" ? competitionLevel : undefined,
      competition_type:
        type === "competition" && competitionType ? competitionType : undefined,
      bracket_participant_ids:
        type === "competition" &&
        competitionType !== "match_up" &&
        orderedBracketParticipants.length > 0
          ? orderedBracketParticipants.map(p => p.id)
          : undefined,
      bracket_data:
        type === "competition" &&
        competitionType !== "match_up" &&
        orderedBracketParticipants.length >= 2
          ? generateBracketFromSlots(competitionType, bracketDesignSlots)
          : undefined,
      matchups:
        type === "competition" && competitionType === "match_up"
          ? matchups
              .filter(m => m.sideA && m.sideB)
              .map(m => ({ sideA: m.sideA, sideB: m.sideB }))
          : undefined,
      metric_type: metricType,
      days_completed_metric_type:
        metricType === "days_completed" ? daysCompletedMetricType : undefined,
      days_completed_threshold_value:
        metricType === "days_completed" && daysCompletedThresholdValue !== ""
          ? parseFloat(daysCompletedThresholdValue)
          : undefined,
      days_completed_excluded_weekdays:
        metricType === "days_completed"
          ? daysCompletedExcludedWeekdays
          : undefined,
      target_value:
        type === "milestone" && prizes[0]?.benchmark_value
          ? parseFloat(prizes[0].benchmark_value)
          : undefined,
      start_date: startDate ? new Date(startDate).toISOString() : undefined,
      end_date: endDate ? new Date(endDate).toISOString() : undefined,
      banner_url: bannerPreview || undefined,
      reward_type: "prize",
      group_ids: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
      additional_rep_ids:
        additionalRepIds.length > 0 ? additionalRepIds : undefined,
      prizes: prizes
        .filter(p => p.title.trim() && p.benchmark_value)
        .map((p, i) => ({
          title: p.title.trim(),
          benchmark_value: parseFloat(p.benchmark_value),
          image_url: p.imagePreview || p.image_url?.trim() || undefined,
          prize_link: p.prize_link?.trim() || undefined,
          first_to_limit:
            p.first_to_limit_enabled && p.first_to_limit
              ? parseInt(p.first_to_limit, 10)
              : undefined,
          sort_order: i,
          // _id is a local React key only — never sent to the API
        })),
    };
    // Only include activate_immediately on create (POST), not on edit (PUT)
    if (!isEditMode) {
      payload.activate_immediately = isDraft ?? false;
    }
    return payload;
  };

  const handleSubmit = async (isDraft = false) => {
    const newErrors = {};
    if (!title.trim()) newErrors.name = "Incentive name is required";
    if (!type) newErrors.type = "Please select an incentive type";
    if (
      metricType === "days_completed" &&
      (daysCompletedThresholdValue === "" ||
        Number(daysCompletedThresholdValue) <= 0)
    ) {
      newErrors.daysCompletedThresholdValue =
        "Days Completed threshold must be greater than 0";
    }
    if (!startDate)
      newErrors.startDate =
        "Start date is required — click Start Date in the Configuration card";
    if (!endDate)
      newErrors.endDate =
        "End date is required — click End Date in the Configuration card";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Open the first missing date modal automatically
      if (newErrors.startDate) setSummaryModal("startDate");
      else if (newErrors.endDate) setSummaryModal("endDate");
      return;
    }
    setSaving(true);
    try {
      const jwt = await getVerifiedJWT();
      const payload = buildPayload(isDraft);
      const url = isEditMode
        ? getApiEndpoint(`/v1/incentives/${incentiveId}`)
        : getApiEndpoint("/v1/incentives");
      const response = await fetch(url, {
        method: isEditMode ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        navigate("/admin?tab=incentives");
      }
    } catch {
      /* silent */
    } finally {
      setSaving(false);
    }
  };

  const isCompetition = type === "competition";
  const isBracketFormat = BRACKET_FORMATS.has(competitionType);
  const showBracketDesignStep = isCompetition && isBracketFormat;

  useEffect(() => {
    if (!showBracketDesignStep || bracketParticipants.length < 2) {
      setBracketDesignSlots([]);
      return;
    }
    setBracketDesignSlots(prev =>
      syncBracketSlots(prev, bracketParticipants, competitionType)
    );
  }, [showBracketDesignStep, bracketParticipants, competitionType]);

  useEffect(() => {
    if (!isBracketFormat) {
      setBracketAddAll(false);
      return;
    }

    const allAvailableIds =
      competitionLevel === "team"
        ? (groups || []).map(g => String(g.id))
        : allDownline.map(r => String(r.user_season_id || r.id));

    if (allAvailableIds.length === 0) {
      return;
    }

    const selectedIds = new Set(bracketParticipants.map(p => String(p.id)));

    const isAllSelected =
      selectedIds.size === allAvailableIds.length &&
      allAvailableIds.every(id => selectedIds.has(id));

    setBracketAddAll(isAllSelected);
  }, [
    isBracketFormat,
    competitionLevel,
    groups,
    allDownline,
    bracketParticipants,
  ]);

  const orderedBracketParticipants =
    bracketDesignSlots.filter(Boolean).length > 0
      ? bracketDesignSlots.filter(Boolean)
      : bracketParticipants;

  const bracketPreviewData = useMemo(
    () => generateBracketFromSlots(competitionType, bracketDesignSlots),
    [competitionType, bracketDesignSlots]
  );

  const bracketRounds = useMemo(() => {
    if (!bracketPreviewData) return [];

    const winnersRounds =
      bracketPreviewData.type === "double_elimination"
        ? bracketPreviewData.winners_bracket?.rounds || []
        : bracketPreviewData.rounds || [];

    return winnersRounds.map((round, roundIdx) => {
      const roundName =
        bracketPreviewData.type === "double_elimination"
          ? `Winners ${round.name}`
          : round.name;
      return {
        label: roundName,
        matchups: round.matchups,
        matchupCount: round.matchups.length,
        isFirstRound: roundIdx === 0,
      };
    });
  }, [bracketPreviewData]);

  const bracketParticipantNameMap = useMemo(() => {
    const map = new Map();
    bracketDesignSlots.filter(Boolean).forEach(p => {
      map.set(String(p.id), p.name || `#${p.id}`);
    });
    bracketParticipants.forEach(p => {
      if (!map.has(String(p.id))) map.set(String(p.id), p.name || `#${p.id}`);
    });
    return map;
  }, [bracketDesignSlots, bracketParticipants]);

  const losersBracketRounds = useMemo(() => {
    if (bracketPreviewData?.type !== "double_elimination") return [];
    return bracketPreviewData.losers_bracket?.rounds || [];
  }, [bracketPreviewData]);

  const displayedWinnersMatchNumbers = useMemo(() => {
    const matchNumberMap = new Map();
    let currentMatchNumber = 0;

    bracketRounds.forEach((round, roundIdx) => {
      for (let matchIdx = 0; matchIdx < round.matchupCount; matchIdx += 1) {
        const isVisible =
          !round.isFirstRound ||
          (bracketDesignSlots[matchIdx * 2] &&
            bracketDesignSlots[matchIdx * 2 + 1]);

        if (!isVisible) continue;

        currentMatchNumber += 1;
        matchNumberMap.set(`${roundIdx}-${matchIdx}`, currentMatchNumber);
      }
    });

    return matchNumberMap;
  }, [bracketRounds, bracketDesignSlots]);

  const totalDisplayedWinnersMatches = displayedWinnersMatchNumbers.size;

  const [bracketPreviewModalOpen, setBracketPreviewModalOpen] = useState(false);
  const [bracketZoom, setBracketZoom] = useState(1);
  const ZOOM_STEP = 0.05;
  const ZOOM_MIN = 0.3;
  const ZOOM_MAX = 2;

  const bracketPreviewBtnRef = useRef(null);
  const [bracketPreviewBtnWidth, setBracketPreviewBtnWidth] = useState(0);

  useLayoutEffect(() => {
    if (bracketPreviewBtnRef.current) {
      const { width } = bracketPreviewBtnRef.current.getBoundingClientRect();
      setBracketPreviewBtnWidth(width);
    }
  }, []);

  const handleBracketDragStart = index => {
    if (!bracketDesignSlots[index]) return;
    setDraggedBracketSlotIndex(index);
  };

  const handleBracketDrop = targetIndex => {
    if (
      draggedBracketSlotIndex == null ||
      draggedBracketSlotIndex === targetIndex
    )
      return;
    setBracketDesignSlots(prev => {
      const next = [...prev];
      [next[draggedBracketSlotIndex], next[targetIndex]] = [
        next[targetIndex],
        next[draggedBracketSlotIndex],
      ];
      return next;
    });
    setDraggedBracketSlotIndex(null);
    setDragOverBracketSlotIndex(null);
  };

  // Steps: competition → MatchUps(0) → Scoring(1)
  //        milestone   → Scoring(0)
  const baseSteps = isCompetition ? (showBracketDesignStep ? 3 : 2) : 1;
  const totalSteps = baseSteps;
  const lastStep = totalSteps - 1;

  const stepName = idx => {
    if (!isCompetition) return ["scoring"][idx] || "scoring";
    if (showBracketDesignStep) {
      return ["matchups", "bracket_design", "scoring"][idx] || "scoring";
    }
    return ["matchups", "scoring"][idx] || "scoring";
  };

  const isMatchUpsValid =
    !isCompetition ||
    (competitionType !== "" &&
      (isBracketFormat
        ? bracketParticipants.length >= 2
        : matchups.every(m => m.sideA && m.sideB)));
  const isScoringValid = metricType && prizes.some(p => p.title.trim());

  const canNext = () => {
    const name = stepName(step);
    if (name === "matchups") return isMatchUpsValid;
    if (name === "bracket_design")
      return orderedBracketParticipants.length >= 2;
    if (name === "scoring") return isScoringValid;
    return true;
  };

  // Page header matching ProgramFormPage style
  const adminHeader = (
    <div
      className="org-page-custom-header"
      style={{ borderBottom: "1px solid #e5e7eb" }}
    >
      <div className="org-page-header-left">
        <h1 className="org-page-main-title">Admin</h1>
        <nav className="org-page-nav-tabs" role="tablist">
          {ADMIN_SECTIONS.map(section => {
            const Icon = section.icon;
            const isActive = section.id === "incentive-creation";
            return (
              <button
                key={section.id}
                role="tab"
                aria-selected={isActive}
                className={`org-page-nav-tab${isActive ? " active" : ""}`}
                onClick={() => section.path && navigate(section.path)}
              >
                <Icon size={16} />
                {section.label}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );

  return (
    <>
      {cropModal && (
        <ImageCropModal
          src={cropModal.src}
          aspectRatio={cropModal.aspectRatio}
          initialPos={cropModal.initialPos}
          onConfirm={cropModal.onConfirm}
          onCancel={() => {
            setCropModal(null);
            setBannerFile(prev =>
              cropModal.aspectRatio === "banner" ? null : prev
            );
            if (bannerInputRef.current) bannerInputRef.current.value = "";
            if (prizeImageInputRef.current)
              prizeImageInputRef.current.value = "";
          }}
        />
      )}

      <PageContent customHeader={adminHeader}>
        <div className="program-details-container" style={{ paddingTop: 0 }}>
          {/* ── Two-column layout ── */}
          <div className="program-details-layout">
            {/* ── Left Sidebar ── */}
            <div className="program-sidebar">
              {/* Incentive Name */}
              <TextInput
                className="incentive-name-input"
                value={title}
                onChange={e => {
                  setTitle(e.target.value);
                  setErrors({});
                }}
                placeholder="Incentive name..."
                error={errors.name}
                styles={{
                  input: {
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    color: "#1a1a1a",
                    border: "none",
                    borderBottom: "2px solid #e5e7eb",
                    borderRadius: 0,
                    padding: "4px 0",
                    background: "transparent",
                    height: "auto",
                  },
                }}
              />

              {/* Banner Upload */}
              <input
                type="file"
                ref={bannerInputRef}
                accept="image/*"
                onChange={handleBannerSelect}
                style={{ display: "none" }}
              />
              {bannerPreview ? (
                <div
                  className="incentive-banner-preview incentive-preview-clickable"
                  onClick={() =>
                    setCropModal({
                      src: bannerPreview,
                      aspectRatio: "banner",
                      initialPos: bannerPosition,
                      onConfirm: pos => {
                        setBannerPosition(pos);
                        setCropModal(null);
                      },
                    })
                  }
                >
                  <img
                    src={bannerPreview}
                    alt="Banner"
                    style={{
                      objectPosition: `${bannerPosition.x}% ${bannerPosition.y}%`,
                    }}
                    draggable={false}
                  />
                  <div className="incentive-preview-edit-hint">
                    ✎ Adjust Crop
                  </div>
                  <ActionIcon
                    className="incentive-img-remove-btn"
                    variant="filled"
                    color="red"
                    size="sm"
                    radius="xl"
                    onClick={e => {
                      e.stopPropagation();
                      removeBanner();
                    }}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                </div>
              ) : (
                <div
                  style={{
                    width: "100%",
                    paddingTop: "56.25%",
                    position: "relative",
                    borderRadius: 10,
                    border: "2px dashed #e5e7eb",
                    overflow: "hidden",
                    cursor: "pointer",
                    background: "#f9fafb",
                    marginBottom: 8,
                  }}
                  onClick={() => bannerInputRef.current?.click()}
                >
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                    }}
                  >
                    <IconUpload size={22} stroke={1.5} color="#9ca3af" />
                    <Text c="dimmed" size="xs" fw={500}>
                      Add banner image
                    </Text>
                  </div>
                </div>
              )}

              {/* Type Selector Card */}
              <div className="progress-card" style={{ padding: "14px 16px" }}>
                <Text size="xs" fw={600} mb="xs" c="dimmed">
                  Incentive Type
                </Text>
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => {
                      setType("milestone");
                      setPrizes([
                        {
                          title: "",
                          imagePreview: null,
                          benchmark_value: "",
                          image_url: "",
                        },
                      ]);
                    }}
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      padding: "10px 8px",
                      border: `2px solid ${type === "milestone" ? "#ff6b35" : "#e5e7eb"}`,
                      borderRadius: 10,
                      background:
                        type === "milestone"
                          ? "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)"
                          : "white",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    <IconTarget size={20} color="#ff6b35" />
                    <span
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: "#1a1a1a",
                      }}
                    >
                      Milestone
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setType("competition");
                      setPrizes([
                        {
                          title: "",
                          imagePreview: null,
                          benchmark_value: "1",
                          image_url: "",
                        },
                      ]);
                    }}
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      padding: "10px 8px",
                      border: `2px solid ${type === "competition" ? "#ff6b35" : "#e5e7eb"}`,
                      borderRadius: 10,
                      background:
                        type === "competition"
                          ? "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)"
                          : "white",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    <IconTrophy size={20} color="#ff6b35" />
                    <span
                      style={{
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        color: "#1a1a1a",
                      }}
                    >
                      Competition
                    </span>
                  </button>
                </div>
              </div>

              {/* Configuration Card — pill style, click to open modal */}
              <div
                className="progress-card"
                style={{
                  padding: "10px 14px",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Text size="xs" fw={600} mb={6} c="dimmed">
                  Configuration
                </Text>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                    justifyContent: "space-between",
                  }}
                >
                  {[
                    {
                      key: "metric",
                      icon: <IconTrophy size={14} color="#ff6b35" />,
                      label: "Metric",
                      value:
                        METRIC_OPTIONS.find(m => m.value === metricType)
                          ?.label || "—",
                    },
                    {
                      key: "tiebreaker",
                      icon: <IconTrophy size={14} color="#ff6b35" />,
                      label: "Tiebreaker",
                      value: tiebreaker.trim() || "—",
                    },
                    {
                      key: "conditions",
                      icon: <IconFileText size={14} color="#ff6b35" />,
                      label: "Conditions",
                      value: conditions.trim() || "—",
                    },
                    {
                      key: "startDate",
                      icon: <IconCalendarEvent size={14} color="#ff6b35" />,
                      label: "Start Date",
                      value: startDate
                        ? new Date(startDate).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "—",
                    },
                    {
                      key: "endDate",
                      icon: <IconCalendarEvent size={14} color="#ff6b35" />,
                      label: "End Date",
                      value: endDate
                        ? new Date(endDate).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "—",
                    },
                  ].map(row => (
                    <div
                      key={row.key}
                      className="insight-row"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        cursor: "pointer",
                        flex: 1,
                        borderBottom: "1px solid #f3f4f6",
                      }}
                      onClick={() => setSummaryModal(row.key)}
                    >
                      <span
                        className="insight-label"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          fontSize: "0.8rem",
                        }}
                      >
                        {row.icon} {row.label}
                      </span>
                      <span
                        className="insight-badge"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary modals */}
              <Modal
                opened={summaryModal === "metric"}
                onClose={() => setSummaryModal(null)}
                title="Scoring Metric"
                size="sm"
                centered
              >
                <Stack gap="xs">
                  {METRIC_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setMetricType(opt.value);
                        setSummaryModal(null);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        padding: "12px 16px",
                        border: `1.5px solid ${metricType === opt.value ? "#ff6b35" : "#e5e7eb"}`,
                        borderRadius: 10,
                        background:
                          metricType === opt.value ? "#fff7ed" : "white",
                        cursor: "pointer",
                        textAlign: "left",
                        fontSize: "0.9rem",
                        fontWeight: metricType === opt.value ? 600 : 400,
                        color: metricType === opt.value ? "#c2410c" : "#374151",
                      }}
                    >
                      {metricType === opt.value && (
                        <IconCheck size={16} color="#ff6b35" />
                      )}
                      {opt.label}
                    </button>
                  ))}
                </Stack>
              </Modal>

              <Modal
                opened={summaryModal === "tiebreaker"}
                onClose={() => setSummaryModal(null)}
                title="Tiebreaker"
                size="sm"
                centered
              >
                <Textarea
                  label="Tiebreaker rule"
                  placeholder="e.g. Most leads created wins the tiebreaker"
                  value={tiebreaker}
                  onChange={e => setTiebreaker(e.target.value)}
                  minRows={3}
                  size="md"
                  autoFocus
                />
                <Button
                  mt="md"
                  fullWidth
                  style={{ background: "#ff6b35", border: "none" }}
                  onClick={() => setSummaryModal(null)}
                >
                  Save
                </Button>
              </Modal>

              <Modal
                opened={summaryModal === "conditions"}
                onClose={() => setSummaryModal(null)}
                title="Conditions"
                size="sm"
                centered
              >
                <Textarea
                  label="Participation conditions"
                  placeholder="e.g. Must be active for the full duration of the incentive"
                  value={conditions}
                  onChange={e => setConditions(e.target.value)}
                  minRows={3}
                  size="md"
                  autoFocus
                />
                <Button
                  mt="md"
                  fullWidth
                  style={{ background: "#ff6b35", border: "none" }}
                  onClick={() => setSummaryModal(null)}
                >
                  Save
                </Button>
              </Modal>

              <Modal
                opened={summaryModal === "startDate"}
                onClose={() => setSummaryModal(null)}
                title="Start Date & Time"
                size="sm"
                centered
              >
                <Stack gap="md">
                  <div>
                    <Text size="sm" fw={500} mb={6}>
                      Pick a start date and time
                    </Text>
                    <input
                      type="datetime-local"
                      value={
                        startDate
                          ? (() => {
                              const d = new Date(startDate);
                              const pad = n => String(n).padStart(2, "0");
                              return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                            })()
                          : ""
                      }
                      onChange={e => {
                        if (e.target.value)
                          setStartDate(new Date(e.target.value));
                      }}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        fontSize: "0.95rem",
                        outline: "none",
                      }}
                    />
                  </div>
                  <Button
                    style={{ background: "#ff6b35", border: "none" }}
                    onClick={() => setSummaryModal(null)}
                    disabled={!startDate}
                  >
                    Confirm
                  </Button>
                </Stack>
              </Modal>

              <Modal
                opened={summaryModal === "endDate"}
                onClose={() => setSummaryModal(null)}
                title="End Date & Time"
                size="sm"
                centered
              >
                <Stack gap="md">
                  <div>
                    <Text size="sm" fw={500} mb={6}>
                      Pick an end date and time
                    </Text>
                    <input
                      type="datetime-local"
                      value={
                        endDate
                          ? (() => {
                              const d = new Date(endDate);
                              const pad = n => String(n).padStart(2, "0");
                              return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                            })()
                          : ""
                      }
                      min={
                        startDate
                          ? (() => {
                              const d = new Date(startDate);
                              const pad = n => String(n).padStart(2, "0");
                              return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                            })()
                          : undefined
                      }
                      onChange={e => {
                        if (e.target.value)
                          setEndDate(new Date(e.target.value));
                      }}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        fontSize: "0.95rem",
                        outline: "none",
                      }}
                    />
                  </div>
                  <Button
                    style={{ background: "#ff6b35", border: "none" }}
                    onClick={() => setSummaryModal(null)}
                    disabled={!endDate}
                  >
                    Confirm
                  </Button>
                </Stack>
              </Modal>
            </div>

            {/* ── Right Content Panel ── */}
            <div className="program-content">
              {/* Stepper */}
              <div style={{ borderBottom: "1px solid #e5e7eb" }}>
                <Stepper
                  active={step}
                  onStepClick={s => {
                    if (s < step) setStep(s);
                    else if (s === step + 1 && canNext()) setStep(s);
                  }}
                  color="orange"
                  size="sm"
                  p="md"
                  pb="md"
                  classNames={{ stepLabel: "incentive-step-label" }}
                >
                  {isCompetition && (
                    <Stepper.Step
                      label="Match Ups"
                      description="Set Competitors"
                    />
                  )}
                  {showBracketDesignStep && (
                    <Stepper.Step
                      label="Bracket Design"
                      description="Arrange Seeds"
                    />
                  )}
                  <Stepper.Step label="Scoring" description="Prizes" />
                </Stepper>
              </div>

              {/* Bracket design step: sticky header + bracket content share one horizontal scroll container.
                  Both the header and the BracketPreview are rendered inside this single overflowX:auto
                  wrapper so that horizontal scrolling moves them together. */}
              {stepName(step) === "bracket_design" && (
                <div style={{ overflowX: "auto", overflowY: "visible" }}>
                  {/* Round header — scrolls with bracket content horizontally */}
                  <div
                    style={{
                      background: "white",
                      borderBottom: "1px solid #e5e7eb",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      padding: "6px 24px",
                      minWidth: "max-content",
                    }}
                  >
                    {/* Zoom controls — left of Round 1 */}
                    <div
                      style={{
                        width: bracketPreviewBtnWidth
                          ? bracketPreviewBtnWidth + 8
                          : 138,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        {/* Zoom controls */}
                        <button
                          onClick={() =>
                            setBracketZoom(z =>
                              Math.max(
                                ZOOM_MIN,
                                parseFloat((z - ZOOM_STEP).toFixed(2))
                              )
                            )
                          }
                          disabled={bracketZoom <= ZOOM_MIN}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            border: "1.5px solid #e5e7eb",
                            borderRadius: 6,
                            background:
                              bracketZoom <= ZOOM_MIN ? "#f9fafb" : "white",
                            cursor:
                              bracketZoom <= ZOOM_MIN
                                ? "not-allowed"
                                : "pointer",
                            fontSize: "1rem",
                            fontWeight: 700,
                            color:
                              bracketZoom <= ZOOM_MIN ? "#d1d5db" : "#374151",
                            lineHeight: 1,
                          }}
                          title="Zoom out"
                        >
                          −
                        </button>
                        <span
                          style={{
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            color: "#6b7280",
                            minWidth: 34,
                            textAlign: "center",
                          }}
                        >
                          {Math.round(bracketZoom * 100)}%
                        </span>
                        <button
                          onClick={() =>
                            setBracketZoom(z =>
                              Math.min(
                                ZOOM_MAX,
                                parseFloat((z + ZOOM_STEP).toFixed(2))
                              )
                            )
                          }
                          disabled={bracketZoom >= ZOOM_MAX}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 28,
                            height: 28,
                            border: "1.5px solid #e5e7eb",
                            borderRadius: 6,
                            background:
                              bracketZoom >= ZOOM_MAX ? "#f9fafb" : "white",
                            cursor:
                              bracketZoom >= ZOOM_MAX
                                ? "not-allowed"
                                : "pointer",
                            fontSize: "1rem",
                            fontWeight: 700,
                            color:
                              bracketZoom >= ZOOM_MAX ? "#d1d5db" : "#374151",
                            lineHeight: 1,
                          }}
                          title="Zoom in"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    {bracketRounds.map((round, roundIdx) => (
                      <div
                        key={`sticky-label-${roundIdx}`}
                        className="bracket-round-label"
                        style={{
                          width: 160 * bracketZoom,
                          flexShrink: 0,
                          marginRight:
                            roundIdx < bracketRounds.length - 1
                              ? 56 * bracketZoom
                              : 0,
                          marginBottom: 0,
                          textAlign: "center",
                          fontSize: `${0.82 * bracketZoom}rem`,
                        }}
                      >
                        {round.label || round.name}
                      </div>
                    ))}
                  </div>
                  {/* Bracket content — inside the same scroll container as the header */}
                  <div
                    className="incentive-form-section"
                    style={{ paddingTop: 0, marginTop: 0 }}
                  >
                    {/* Zoom wrapper: transform-origin top left so bracket scales from top-left corner */}
                    <div
                      style={{
                        transformOrigin: "top left",
                        transform: `scale(${bracketZoom})`,
                        // Reserve space for the scaled content so the scroll container knows the real size
                        marginBottom: `calc(${bracketZoom} * 100% - 100%)`,
                      }}
                    >
                      <div
                        style={{
                          paddingTop: 4,
                          paddingBottom: 8,
                          display: "flex",
                          flexDirection: "row",
                        }}
                      >
                        {/* Empty spacer matching the Preview Bracket button width + marginRight so bracket aligns under round labels */}
                        <div
                          style={{
                            width: bracketPreviewBtnWidth
                              ? bracketPreviewBtnWidth + 16
                              : 146,
                            flexShrink: 0,
                          }}
                        />
                        <BracketPreview
                          bracketRounds={bracketRounds}
                          bracketDesignSlots={bracketDesignSlots}
                          bracketParticipantNameMap={bracketParticipantNameMap}
                          displayedWinnersMatchNumbers={
                            displayedWinnersMatchNumbers
                          }
                          competitionType={competitionType}
                          losersBracketRounds={losersBracketRounds}
                          totalDisplayedWinnersMatches={
                            totalDisplayedWinnersMatches
                          }
                          interactive={true}
                          draggedBracketSlotIndex={draggedBracketSlotIndex}
                          dragOverBracketSlotIndex={dragOverBracketSlotIndex}
                          onSlotDragStart={handleBracketDragStart}
                          onSlotDragOver={idx =>
                            setDragOverBracketSlotIndex(idx)
                          }
                          onSlotDragLeave={() =>
                            setDragOverBracketSlotIndex(null)
                          }
                          onSlotDrop={handleBracketDrop}
                          onSlotDragEnd={() => {
                            setDraggedBracketSlotIndex(null);
                            setDragOverBracketSlotIndex(null);
                          }}
                        />
                      </div>
                    </div>
                    {/* end zoom wrapper */}
                  </div>
                </div>
              )}

              {/* ── Scrollable Form Content ── */}
              <div className="tasks-list">
                {/* Step 0 (competition) - Match Ups */}
                {stepName(step) === "matchups" && isCompetition && (
                  <div>
                    {/* Competition Level — first */}
                    <div className="incentive-form-section">
                      <label>Competition Level</label>
                      <div className="incentive-type-selector">
                        <div
                          className={`incentive-type-card ${competitionLevel === "individual" ? "selected" : ""}`}
                          onClick={() => {
                            setCompetitionLevel("individual");
                            setMatchups([
                              { id: Date.now(), sideA: null, sideB: null },
                            ]);
                            setMatchupRepSearch(null);
                          }}
                        >
                          <IconUser size={24} color="#ff6b35" />
                          <h4>Individual</h4>
                          <p>Person vs person</p>
                        </div>
                        <div
                          className={`incentive-type-card ${competitionLevel === "team" ? "selected" : ""}`}
                          onClick={() => {
                            setCompetitionLevel("team");
                            setMatchups([
                              { id: Date.now(), sideA: null, sideB: null },
                            ]);
                            setMatchupRepSearch(null);
                          }}
                        >
                          <IconUsers size={24} color="#ff6b35" />
                          <h4>Team</h4>
                          <p>Group vs group</p>
                        </div>
                      </div>
                    </div>

                    {/* HIDDEN: dead block replaced by bracket picker below */}
                    {false && (
                      <div className="incentive-form-section">
                        <label>
                          {competitionLevel === "team"
                            ? "Add Teams to Bracket"
                            : "Add Participants to Bracket"}
                        </label>
                        <Text size="xs" c="dimmed" mb="xs">
                          {competitionLevel === "team"
                            ? "Add the groups that will compete. The bracket is auto-generated on save."
                            : "Add the reps that will compete. The bracket is auto-generated on save."}
                        </Text>

                        {/* Groups Added section — shown when groups have been added via group search */}
                        {bracketAddedGroups.length > 0 && (
                          <div
                            style={{
                              background: "#f9fafb",
                              border: "1px solid #e9ecef",
                              borderRadius: 10,
                              padding: "10px 14px",
                              marginBottom: 10,
                            }}
                          >
                            <Text size="xs" fw={600} c="dimmed" mb={6}>
                              Groups Added ({bracketAddedGroups.length})
                            </Text>
                            <div className="incentive-rep-chips">
                              {bracketAddedGroups.map(g => (
                                <div
                                  key={g.id}
                                  className="incentive-rep-chip group"
                                >
                                  <IconUsers size={13} />
                                  <span>{g.name}</span>
                                  <ActionIcon
                                    size="xs"
                                    variant="subtle"
                                    color="blue"
                                    onClick={() => removeGroupFromBracket(g.id)}
                                    ml={4}
                                  >
                                    <IconX size={11} />
                                  </ActionIcon>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {bracketParticipants.length > 0 && (
                          <div
                            className="incentive-rep-chips"
                            style={{ marginBottom: 10 }}
                          >
                            {bracketParticipants.map(p => (
                              <div
                                key={p.id}
                                className="incentive-rep-chip added"
                              >
                                {competitionLevel === "team" ? (
                                  <IconUsers size={13} />
                                ) : (
                                  <IconUser size={13} />
                                )}
                                <span>{p.name}</span>
                                <ActionIcon
                                  size="xs"
                                  variant="subtle"
                                  color="red"
                                  onClick={() =>
                                    setBracketParticipants(prev =>
                                      prev.filter(x => x.id !== p.id)
                                    )
                                  }
                                  ml={4}
                                >
                                  <IconX size={11} />
                                </ActionIcon>
                              </div>
                            ))}
                          </div>
                        )}
                        <TextInput
                          placeholder={
                            competitionLevel === "team"
                              ? "Search groups..."
                              : "Search reps by name..."
                          }
                          leftSection={<IconSearch size={14} />}
                          size="sm"
                          value={bracketParticipantSearch}
                          onChange={e =>
                            setBracketParticipantSearch(e.target.value)
                          }
                        />
                        {bracketParticipantSearch.trim() && (
                          <div
                            className="incentive-search-results"
                            style={{ marginTop: 2 }}
                          >
                            {competitionLevel === "team"
                              ? (groups || [])
                                  .filter(
                                    g =>
                                      g.name
                                        .toLowerCase()
                                        .includes(
                                          bracketParticipantSearch.toLowerCase()
                                        ) &&
                                      !bracketParticipants.some(
                                        p => p.id === g.id
                                      )
                                  )
                                  .slice(0, 8)
                                  .map(g => (
                                    <div
                                      key={g.id}
                                      className="incentive-search-result"
                                      onClick={() => {
                                        setBracketParticipants(prev => [
                                          ...prev,
                                          { id: g.id, name: g.name },
                                        ]);
                                        setBracketParticipantSearch("");
                                      }}
                                    >
                                      <IconUsers size={15} color="#868e96" />
                                      <Text
                                        size="sm"
                                        fw={500}
                                        style={{ flex: 1 }}
                                      >
                                        {g.name}
                                      </Text>
                                      <IconPlus size={14} color="#ff6b35" />
                                    </div>
                                  ))
                              : allDownline
                                  .filter(r => {
                                    const id = r.user_season_id || r.id;
                                    const term =
                                      bracketParticipantSearch.toLowerCase();
                                    const name =
                                      `${r.first_name || ""} ${r.last_name || ""}`.toLowerCase();
                                    return (
                                      name.includes(term) &&
                                      !bracketParticipants.some(
                                        p => p.id === id
                                      )
                                    );
                                  })
                                  .slice(0, 8)
                                  .map(r => {
                                    const id = r.user_season_id || r.id;
                                    return (
                                      <div
                                        key={id}
                                        className="incentive-search-result"
                                        onClick={() => {
                                          setBracketParticipants(prev => [
                                            ...prev,
                                            {
                                              id,
                                              name: `${r.first_name} ${r.last_name}`,
                                            },
                                          ]);
                                          setBracketParticipantSearch("");
                                        }}
                                      >
                                        <IconUser size={15} color="#868e96" />
                                        <div style={{ flex: 1 }}>
                                          <Text size="sm" fw={500}>
                                            {r.first_name} {r.last_name}
                                          </Text>
                                        </div>
                                        <IconPlus size={14} color="#ff6b35" />
                                      </div>
                                    );
                                  })}
                          </div>
                        )}
                        {bracketParticipants.length < 2 && (
                          <Text size="xs" c="dimmed" mt="xs" fs="italic">
                            Add at least 2{" "}
                            {competitionLevel === "team"
                              ? "teams"
                              : "participants"}{" "}
                            to enable Next.
                          </Text>
                        )}
                      </div>
                    )}

                    {/* Competition Format */}
                    <div className="incentive-form-section">
                      <label>Competition Format</label>
                      <Text size="xs" c="dimmed" mb="sm">
                        Choose how competitors are matched up
                      </Text>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(160px, 1fr))",
                          gap: 10,
                        }}
                      >
                        {[
                          {
                            value: "single_elimination",
                            label: "Single Elimination Bracket",
                            description: "Lose once and you're out",
                          },
                          {
                            value: "double_elimination",
                            label: "Double Elimination Bracket",
                            description: "Two losses to be eliminated",
                          },
                          {
                            value: "king_of_the_hill",
                            label: "King of the Hill",
                            description: "Top scorer holds the crown",
                          },
                          {
                            value: "match_up",
                            label: "Match Up",
                            description: "Head-to-head paired matchups",
                          },
                        ].map(opt => (
                          <div
                            key={opt.value}
                            className={`incentive-type-card${competitionType === opt.value ? " selected" : ""}`}
                            style={{ padding: "12px 10px" }}
                            onClick={() => setCompetitionType(opt.value)}
                          >
                            <IconTrophy
                              size={20}
                              color={
                                competitionType === opt.value
                                  ? "#ff6b35"
                                  : "#9ca3af"
                              }
                            />
                            <h4 style={{ fontSize: "0.8rem" }}>{opt.label}</h4>
                            <p style={{ fontSize: "0.72rem" }}>
                              {opt.description}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Bracket Participant Picker — shown after format */}
                    {isBracketFormat && (
                      <div className="incentive-form-section">
                        <label style={{ margin: 0, marginBottom: 6 }}>
                          {competitionLevel === "team"
                            ? "Add Teams to Bracket"
                            : "Add Participants to Bracket"}
                        </label>
                        <div className="incentive-bracket-participant-controls">
                          <button
                            type="button"
                            className={`incentive-bracket-add-all-btn${bracketAddAll ? " active" : ""}`}
                            onClick={() => {
                              const checked = !bracketAddAll;
                              setBracketAddAll(checked);
                              if (checked) {
                                if (competitionLevel === "team") {
                                  setBracketParticipants(
                                    (groups || []).map(g => ({
                                      id: g.id,
                                      name: g.name,
                                    }))
                                  );
                                } else {
                                  setBracketParticipants(
                                    allDownline.map(r => ({
                                      id: r.user_season_id || r.id,
                                      name: `${r.first_name || ""} ${r.last_name || ""}`.trim(),
                                    }))
                                  );
                                }
                              } else {
                                setBracketParticipants([]);
                              }
                            }}
                          >
                            <span className="incentive-bracket-add-all-label">
                              {competitionLevel === "team"
                                ? "Add all groups"
                                : "Add everyone"}
                            </span>
                            <span className="incentive-bracket-add-all-track">
                              <span className="incentive-bracket-add-all-thumb" />
                            </span>
                          </button>
                          <div className="incentive-bracket-controls-divider" />
                          <div className="incentive-bracket-search-label">
                            Search by:
                          </div>
                          <div className="incentive-bracket-search-row">
                            <div className="incentive-bracket-mode-tabs">
                              {[
                                { value: "rep", label: "Rep" },
                                { value: "group", label: "Group" },
                                { value: "team", label: "Team" },
                              ].map(option => (
                                <button
                                  key={option.value}
                                  type="button"
                                  className={`incentive-bracket-mode-tab ${bracketCompetitorMode === option.value ? "active" : ""}`}
                                  onClick={() => {
                                    setBracketCompetitorMode(option.value);
                                    setBracketParticipantSearch("");
                                  }}
                                >
                                  {option.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <TextInput
                            className="incentive-bracket-search-input"
                            placeholder={
                              bracketCompetitorMode === "rep"
                                ? "Search reps by name..."
                                : bracketCompetitorMode === "group"
                                  ? "Search groups by name..."
                                  : "Search teams by name..."
                            }
                            leftSection={<IconSearch size={14} />}
                            size="sm"
                            value={bracketParticipantSearch}
                            onChange={e =>
                              setBracketParticipantSearch(e.target.value)
                            }
                          />
                        </div>
                        {bracketCompetitorSearchResults.length > 0 && (
                          <div
                            className="incentive-search-results"
                            style={{ marginTop: 2 }}
                          >
                            {bracketCompetitorSearchResults.map(item => (
                              <div
                                key={`${item.type}-${item.id}`}
                                className="incentive-search-result"
                                onClick={() =>
                                  item.type === "rep"
                                    ? addBracketRep(item.id)
                                    : addBracketGroupOrTeam(
                                        item.id,
                                        item.type !== "rep"
                                      )
                                }
                              >
                                {item.type === "rep" ? (
                                  <IconUser size={15} color="#868e96" />
                                ) : (
                                  <IconUsers size={15} color="#868e96" />
                                )}
                                <div style={{ flex: 1 }}>
                                  <Text size="sm" fw={500}>
                                    {item.label}
                                  </Text>
                                  {item.type !== "rep" && (
                                    <Text size="xs" c="dimmed">
                                      Add all reps from this {item.type}
                                    </Text>
                                  )}
                                </div>
                                <IconPlus size={14} color="#ff6b35" />
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Groups Added section — group chip + member rep chips */}
                        <div style={{ marginBottom: 10 }}>
                          <Text size="xs" fw={600} c="dimmed" mb={6}>
                            Groups Added ({bracketAddedGroups.length})
                          </Text>
                          {bracketAddedGroups.length === 0 ? (
                            <Text size="xs" c="dimmed" fs="italic">
                              No groups added yet.
                            </Text>
                          ) : (
                            bracketAddedGroups.map(g => {
                              const groupRepsInBracket =
                                getBracketGroupRepChips(g.id);
                              return (
                                <div key={g.id} style={{ marginBottom: 8 }}>
                                  {/* Group chip */}
                                  <div
                                    className="incentive-rep-chips"
                                    style={{ marginBottom: 4 }}
                                  >
                                    <div className="incentive-rep-chip group">
                                      <IconUsers size={13} />
                                      <span>{g.name}</span>
                                      <ActionIcon
                                        size="xs"
                                        variant="subtle"
                                        color="blue"
                                        onClick={() =>
                                          removeGroupFromBracket(g.id)
                                        }
                                        ml={4}
                                      >
                                        <IconX size={11} />
                                      </ActionIcon>
                                    </div>
                                  </div>
                                  {/* Rep chips for members of this group */}
                                  {groupRepsInBracket.length > 0 && (
                                    <div
                                      className="incentive-rep-chips"
                                      style={{ paddingLeft: 12 }}
                                    >
                                      {groupRepsInBracket.map(p => (
                                        <div
                                          key={p.id}
                                          className="incentive-rep-chip added"
                                        >
                                          <IconUser size={13} />
                                          <span>{p.name}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>

                        {/* Individually added reps (not from a group) */}
                        {(() => {
                          const groupMemberIds = new Set(
                            bracketAddedGroups.flatMap(g => g.memberIds || [])
                          );
                          const soloReps = bracketParticipants.filter(
                            p => !groupMemberIds.has(String(p.id))
                          );
                          return (
                            <div style={{ marginBottom: 10 }}>
                              <Text size="xs" fw={600} c="dimmed" mb={6}>
                                Reps Added ({soloReps.length})
                              </Text>
                              {soloReps.length === 0 ? (
                                <Text size="xs" c="dimmed" fs="italic">
                                  No reps added yet.
                                </Text>
                              ) : (
                                <div className="incentive-rep-chips">
                                  {soloReps.map(p => (
                                    <div
                                      key={p.id}
                                      className="incentive-rep-chip added"
                                    >
                                      <IconUser size={13} />
                                      <span>{p.name}</span>
                                      <ActionIcon
                                        size="xs"
                                        variant="subtle"
                                        color="red"
                                        onClick={() =>
                                          setBracketParticipants(prev =>
                                            prev.filter(x => x.id !== p.id)
                                          )
                                        }
                                        ml={4}
                                      >
                                        <IconX size={11} />
                                      </ActionIcon>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                        {bracketParticipants.length < 2 && (
                          <Text size="xs" c="dimmed" mt="xs" fs="italic">
                            Add at least 2{" "}
                            {competitionLevel === "team"
                              ? "teams"
                              : "participants"}{" "}
                            to enable Next.
                          </Text>
                        )}
                      </div>
                    )}

                    {competitionType === "match_up" && (
                      <div className="incentive-form-section">
                        <label style={{ marginBottom: 4 }}>
                          {competitionLevel === "team"
                            ? "Team Match Ups"
                            : "Individual Match Ups"}
                        </label>
                        <Text size="xs" c="dimmed" mb="sm">
                          {competitionLevel === "team"
                            ? "Pair groups against each other."
                            : "Pair individual reps against each other."}
                        </Text>
                        <Stack gap="xs">
                          {matchups.map((matchup, idx) => (
                            <div
                              key={matchup.id}
                              className="incentive-matchup-row"
                            >
                              <Text
                                size="xs"
                                c="dimmed"
                                fw={600}
                                className="incentive-matchup-row-label"
                              >
                                #{idx + 1}
                              </Text>
                              <div className="incentive-matchup-side">
                                {competitionLevel === "team" ? (
                                  <Select
                                    placeholder="Pick group..."
                                    data={
                                      groups
                                        ? groups
                                            .filter(g => g.id !== matchup.sideB)
                                            .map(g => ({
                                              value: String(g.id),
                                              label: g.name,
                                            }))
                                        : []
                                    }
                                    value={
                                      matchup.sideA
                                        ? String(matchup.sideA)
                                        : null
                                    }
                                    onChange={val =>
                                      updateMatchupSide(
                                        matchup.id,
                                        "sideA",
                                        val ? Number(val) || val : null
                                      )
                                    }
                                    size="sm"
                                    leftSection={<IconUsers size={14} />}
                                    clearable
                                  />
                                ) : (
                                  <div
                                    className="incentive-matchup-rep-selector"
                                    ref={el => {
                                      repSelectorRefs.current[
                                        `${matchup.id}-sideA`
                                      ] = el;
                                    }}
                                  >
                                    {matchup.sideA ? (
                                      <div className="incentive-matchup-pill">
                                        <IconUser size={13} />
                                        <span>
                                          {(() => {
                                            const r = allDownline.find(
                                              r =>
                                                (r.user_season_id || r.id) ===
                                                matchup.sideA
                                            );
                                            return r
                                              ? `${r.first_name} ${r.last_name}`
                                              : "Rep";
                                          })()}
                                        </span>
                                        <ActionIcon
                                          size="xs"
                                          variant="subtle"
                                          color="red"
                                          onClick={() =>
                                            updateMatchupSide(
                                              matchup.id,
                                              "sideA",
                                              null
                                            )
                                          }
                                        >
                                          <IconX size={11} />
                                        </ActionIcon>
                                      </div>
                                    ) : (
                                      <TextInput
                                        placeholder="Search rep..."
                                        size="sm"
                                        leftSection={<IconSearch size={13} />}
                                        value={
                                          matchupRepSearch?.matchupId ===
                                            matchup.id &&
                                          matchupRepSearch?.side === "sideA"
                                            ? matchupRepSearch.term
                                            : ""
                                        }
                                        onFocus={() =>
                                          openMatchupSearch(matchup.id, "sideA")
                                        }
                                        onBlur={closeMatchupSearch}
                                        onChange={e =>
                                          setMatchupRepSearch({
                                            matchupId: matchup.id,
                                            side: "sideA",
                                            term: e.target.value,
                                          })
                                        }
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="incentive-matchup-vs">
                                <div className="incentive-vs-badge">VS</div>
                              </div>
                              <div className="incentive-matchup-side">
                                {competitionLevel === "team" ? (
                                  <Select
                                    placeholder="Pick group..."
                                    data={
                                      groups
                                        ? groups
                                            .filter(g => g.id !== matchup.sideA)
                                            .map(g => ({
                                              value: String(g.id),
                                              label: g.name,
                                            }))
                                        : []
                                    }
                                    value={
                                      matchup.sideB
                                        ? String(matchup.sideB)
                                        : null
                                    }
                                    onChange={val =>
                                      updateMatchupSide(
                                        matchup.id,
                                        "sideB",
                                        val ? Number(val) || val : null
                                      )
                                    }
                                    size="sm"
                                    leftSection={<IconUsers size={14} />}
                                    clearable
                                  />
                                ) : (
                                  <div
                                    className="incentive-matchup-rep-selector"
                                    ref={el => {
                                      repSelectorRefs.current[
                                        `${matchup.id}-sideB`
                                      ] = el;
                                    }}
                                  >
                                    {matchup.sideB ? (
                                      <div className="incentive-matchup-pill">
                                        <IconUser size={13} />
                                        <span>
                                          {(() => {
                                            const r = allDownline.find(
                                              r =>
                                                (r.user_season_id || r.id) ===
                                                matchup.sideB
                                            );
                                            return r
                                              ? `${r.first_name} ${r.last_name}`
                                              : "Rep";
                                          })()}
                                        </span>
                                        <ActionIcon
                                          size="xs"
                                          variant="subtle"
                                          color="red"
                                          onClick={() =>
                                            updateMatchupSide(
                                              matchup.id,
                                              "sideB",
                                              null
                                            )
                                          }
                                        >
                                          <IconX size={11} />
                                        </ActionIcon>
                                      </div>
                                    ) : (
                                      <TextInput
                                        placeholder="Search rep..."
                                        size="sm"
                                        leftSection={<IconSearch size={13} />}
                                        value={
                                          matchupRepSearch?.matchupId ===
                                            matchup.id &&
                                          matchupRepSearch?.side === "sideB"
                                            ? matchupRepSearch.term
                                            : ""
                                        }
                                        onFocus={() =>
                                          openMatchupSearch(matchup.id, "sideB")
                                        }
                                        onBlur={closeMatchupSearch}
                                        onChange={e =>
                                          setMatchupRepSearch({
                                            matchupId: matchup.id,
                                            side: "sideB",
                                            term: e.target.value,
                                          })
                                        }
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                              {matchups.length > 1 && (
                                <ActionIcon
                                  variant="subtle"
                                  color="red"
                                  size="sm"
                                  onClick={() => removeMatchup(matchup.id)}
                                >
                                  <IconTrash size={14} />
                                </ActionIcon>
                              )}
                            </div>
                          ))}
                        </Stack>
                        <Button
                          variant="light"
                          color="orange"
                          leftSection={<IconPlus size={14} />}
                          onClick={addMatchup}
                          mt="sm"
                          fullWidth
                        >
                          Add Match Up
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Hidden prize image file input */}
                <input
                  type="file"
                  ref={prizeImageInputRef}
                  accept="image/*"
                  onChange={handlePrizeImageSelect}
                  style={{ display: "none" }}
                />

                {/* Step - Scoring & Prizes */}
                {stepName(step) === "scoring" && (
                  <div>
                    {metricType === "days_completed" && (
                      <div className="incentive-form-section">
                        <label>Days Completed Settings</label>
                        <Text size="xs" c="dimmed" mb="xs">
                          Counts days where each rep completes at least this
                          many training minutes.
                        </Text>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-end",
                            gap: 12,
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <Select
                              label="Track day completion by"
                              data={DAYS_COMPLETED_BASE_METRIC_OPTIONS}
                              value={daysCompletedMetricType}
                              onChange={val =>
                                setDaysCompletedMetricType(
                                  val || "training_minutes"
                                )
                              }
                              size="sm"
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <NumberInput
                              label="Min. minutes per day"
                              placeholder="e.g. 5"
                              value={daysCompletedThresholdValue}
                              onChange={val =>
                                setDaysCompletedThresholdValue(
                                  val === "" || val == null ? "" : String(val)
                                )
                              }
                              min={1}
                              size="sm"
                              error={errors.daysCompletedThresholdValue}
                            />
                          </div>
                          <div style={{ flex: 1 }}>
                            <MultiSelect
                              label="Exclude days:"
                              data={WEEKDAY_OPTIONS.map(d => ({
                                value: String(d.value),
                                label: d.label,
                              }))}
                              value={daysCompletedExcludedWeekdays.map(String)}
                              onChange={vals =>
                                setDaysCompletedExcludedWeekdays(
                                  vals.map(Number)
                                )
                              }
                              placeholder="None"
                              size="sm"
                              clearable
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {type === "milestone" && (
                      <div className="incentive-form-section">
                        <label
                          style={{
                            margin: 0,
                            marginBottom: 4,
                            display: "block",
                          }}
                        >
                          Targets & Prizes
                        </label>
                        <Text size="xs" c="dimmed" mb="sm">
                          Set milestone targets. Users who hit each target earn
                          that prize.
                        </Text>
                        <Stack gap="sm">
                          {prizes.map((prize, index) => (
                            <div
                              key={prize._id ?? index}
                              className="incentive-prize-card"
                            >
                              {prizes.length > 1 && (
                                <ActionIcon
                                  className="remove-prize"
                                  variant="subtle"
                                  color="red"
                                  size="sm"
                                  onClick={() => removePrize(index)}
                                >
                                  <IconTrash size={14} />
                                </ActionIcon>
                              )}
                              <Badge size="sm" color="orange" mb="sm">
                                Tier {index + 1}
                              </Badge>
                              <div className="incentive-prize-card-grid">
                                <div className="incentive-prize-card-left">
                                  {/* 2-col grid: Prize Name + Description */}
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr",
                                      gap: 8,
                                    }}
                                  >
                                    <TextInput
                                      label="Prize Name"
                                      placeholder="e.g. $50 Gift Card"
                                      value={prize.title}
                                      onChange={e =>
                                        updatePrize(
                                          index,
                                          "title",
                                          e.target.value
                                        )
                                      }
                                      size="sm"
                                    />
                                    <TextInput
                                      label="Description or Link (Optional)"
                                      placeholder="e.g. https://amazon.com/..."
                                      value={prize.prize_link || ""}
                                      onChange={e =>
                                        updatePrize(
                                          index,
                                          "prize_link",
                                          e.target.value
                                        )
                                      }
                                      size="sm"
                                    />
                                    <div onBlur={handleBenchmarkBlur}>
                                      <NumberInput
                                        label="Target Value"
                                        placeholder={`e.g. ${(index + 1) * 100}`}
                                        value={prize.benchmark_value}
                                        onChange={val =>
                                          updatePrize(
                                            index,
                                            "benchmark_value",
                                            val
                                          )
                                        }
                                        min={0}
                                        size="sm"
                                      />
                                    </div>
                                    <div className="incentive-firstlimit-controls">
                                      <Checkbox
                                        label="First to limit"
                                        labelPosition="left"
                                        size="sm"
                                        color="orange"
                                        checked={
                                          prize.first_to_limit_enabled || false
                                        }
                                        onChange={e =>
                                          updatePrize(
                                            index,
                                            "first_to_limit_enabled",
                                            e.currentTarget.checked
                                          )
                                        }
                                        styles={{
                                          label: {
                                            fontSize: "0.875rem",
                                            fontWeight: 600,
                                            color: "#374151",
                                          },
                                        }}
                                      />
                                      <NumberInput
                                        placeholder="# winners"
                                        value={prize.first_to_limit}
                                        onChange={val =>
                                          updatePrize(
                                            index,
                                            "first_to_limit",
                                            val
                                          )
                                        }
                                        min={1}
                                        size="sm"
                                        disabled={!prize.first_to_limit_enabled}
                                        styles={{
                                          input: {
                                            background:
                                              prize.first_to_limit_enabled
                                                ? undefined
                                                : "#f3f4f6",
                                            color: prize.first_to_limit_enabled
                                              ? undefined
                                              : "#9ca3af",
                                          },
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                                {prize.imagePreview ? (
                                  <div
                                    className="incentive-prize-image-preview incentive-preview-clickable"
                                    onClick={() => {
                                      const idx = index;
                                      setCropModal({
                                        src: prize.imagePreview,
                                        aspectRatio: "prize",
                                        initialPos: prize.imagePosition || {
                                          x: 50,
                                          y: 50,
                                        },
                                        onConfirm: pos => {
                                          updatePrize(
                                            idx,
                                            "imagePosition",
                                            pos
                                          );
                                          setCropModal(null);
                                        },
                                      });
                                    }}
                                  >
                                    <img
                                      src={prize.imagePreview}
                                      alt="Prize"
                                      style={{
                                        objectPosition: `${prize.imagePosition?.x ?? 50}% ${prize.imagePosition?.y ?? 50}%`,
                                      }}
                                      draggable={false}
                                    />
                                    <div className="incentive-preview-edit-hint">
                                      ✎ Adjust Crop
                                    </div>
                                    <ActionIcon
                                      className="incentive-prize-image-remove"
                                      variant="filled"
                                      color="red"
                                      size="xs"
                                      radius="xl"
                                      onClick={e => {
                                        e.stopPropagation();
                                        updatePrize(
                                          index,
                                          "imagePreview",
                                          null
                                        );
                                      }}
                                    >
                                      <IconX size={10} />
                                    </ActionIcon>
                                  </div>
                                ) : (
                                  <div
                                    className="incentive-prize-image-upload"
                                    onClick={() => {
                                      setActivePrizeImageIndex(index);
                                      prizeImageInputRef.current?.click();
                                    }}
                                  >
                                    <IconUpload size={20} />
                                    <span>Photo</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </Stack>
                        <Button
                          variant="light"
                          color="orange"
                          leftSection={<IconPlus size={14} />}
                          onClick={addPrize}
                          style={{ display: "flex", margin: "12px auto 0" }}
                        >
                          Add Tier
                        </Button>
                      </div>
                    )}

                    {type === "competition" && (
                      <div className="incentive-form-section">
                        <label
                          style={{
                            margin: 0,
                            marginBottom: 4,
                            display: "block",
                          }}
                        >
                          Competition Prizes
                        </label>
                        <Text size="xs" c="dimmed" mb="sm">
                          Award prizes by finishing place (1st, 2nd...) or by
                          reaching a round (Round of 16, etc.).
                        </Text>
                        <Stack gap="sm">
                          {prizes.map((prize, index) => (
                            <div
                              key={index}
                              className="incentive-prize-card competition-prize"
                            >
                              {prizes.length > 1 && (
                                <ActionIcon
                                  className="remove-prize"
                                  variant="subtle"
                                  color="red"
                                  size="sm"
                                  onClick={() => removePrize(index)}
                                >
                                  <IconTrash size={14} />
                                </ActionIcon>
                              )}
                              {/* Prize type selector */}
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  marginBottom: 10,
                                }}
                              >
                                <button
                                  onClick={() =>
                                    updatePrize(index, "prize_type", "place")
                                  }
                                  style={{
                                    flex: 1,
                                    padding: "6px 10px",
                                    border: `1.5px solid ${!prize.prize_type || prize.prize_type === "place" ? "#ff6b35" : "#e5e7eb"}`,
                                    borderRadius: 8,
                                    background:
                                      !prize.prize_type ||
                                      prize.prize_type === "place"
                                        ? "#fff7ed"
                                        : "white",
                                    cursor: "pointer",
                                    fontSize: "0.78rem",
                                    fontWeight: 600,
                                    color:
                                      !prize.prize_type ||
                                      prize.prize_type === "place"
                                        ? "#c2410c"
                                        : "#6b7280",
                                  }}
                                >
                                  By Place
                                </button>
                                <button
                                  onClick={() =>
                                    updatePrize(index, "prize_type", "round")
                                  }
                                  style={{
                                    flex: 1,
                                    padding: "6px 10px",
                                    border: `1.5px solid ${prize.prize_type === "round" ? "#ff6b35" : "#e5e7eb"}`,
                                    borderRadius: 8,
                                    background:
                                      prize.prize_type === "round"
                                        ? "#fff7ed"
                                        : "white",
                                    cursor: "pointer",
                                    fontSize: "0.78rem",
                                    fontWeight: 600,
                                    color:
                                      prize.prize_type === "round"
                                        ? "#c2410c"
                                        : "#6b7280",
                                  }}
                                >
                                  By Round
                                </button>
                              </div>
                              {/* Place or Round badge */}
                              <div className="competition-place-header">
                                {!prize.prize_type ||
                                prize.prize_type === "place" ? (
                                  <Select
                                    placeholder="Select place..."
                                    data={[
                                      { value: "1", label: "1st Place" },
                                      { value: "2", label: "2nd Place" },
                                      { value: "3", label: "3rd Place" },
                                      { value: "4", label: "4th Place" },
                                      { value: "5", label: "5th Place" },
                                      { value: "6", label: "6th Place" },
                                      { value: "7", label: "7th Place" },
                                      { value: "8", label: "8th Place" },
                                      { value: "9", label: "9th Place" },
                                      { value: "10", label: "10th Place" },
                                    ]}
                                    value={
                                      prize.benchmark_value
                                        ? String(prize.benchmark_value)
                                        : null
                                    }
                                    onChange={val =>
                                      updatePrize(
                                        index,
                                        "benchmark_value",
                                        val || ""
                                      )
                                    }
                                    size="sm"
                                    style={{ flex: 1 }}
                                  />
                                ) : (
                                  <Select
                                    placeholder="Select round..."
                                    data={
                                      bracketRounds.length > 0
                                        ? [...bracketRounds]
                                            .reverse()
                                            .map((round, revIdx) => ({
                                              value: String(
                                                bracketRounds.length -
                                                  1 -
                                                  revIdx
                                              ),
                                              label: round.label || round.name,
                                            }))
                                        : [
                                            {
                                              value: "",
                                              label:
                                                "No bracket yet — add participants first",
                                            },
                                          ]
                                    }
                                    value={
                                      prize.prize_round != null
                                        ? String(prize.prize_round)
                                        : null
                                    }
                                    onChange={val =>
                                      updatePrize(
                                        index,
                                        "prize_round",
                                        val != null ? val : null
                                      )
                                    }
                                    size="sm"
                                    style={{ flex: 1 }}
                                  />
                                )}
                              </div>
                              <div style={{ marginTop: 8 }}>
                                <div className="incentive-prize-name-row">
                                  <div
                                    style={{
                                      flex: 1,
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 8,
                                    }}
                                  >
                                    <TextInput
                                      label="Prize Name"
                                      placeholder="What do they win?"
                                      value={prize.title}
                                      onChange={e =>
                                        updatePrize(
                                          index,
                                          "title",
                                          e.target.value
                                        )
                                      }
                                      size="sm"
                                    />
                                    <TextInput
                                      label="Prize Description or Link (Optional)"
                                      placeholder="e.g. https://amazon.com/... or 'Weekend trip to Vegas'"
                                      value={prize.prize_link || ""}
                                      onChange={e =>
                                        updatePrize(
                                          index,
                                          "prize_link",
                                          e.target.value
                                        )
                                      }
                                      size="sm"
                                    />
                                  </div>
                                  {prize.imagePreview ? (
                                    <div
                                      className="incentive-prize-image-preview incentive-preview-clickable"
                                      onClick={() => {
                                        const idx = index;
                                        setCropModal({
                                          src: prize.imagePreview,
                                          aspectRatio: "prize",
                                          initialPos: prize.imagePosition || {
                                            x: 50,
                                            y: 50,
                                          },
                                          onConfirm: pos => {
                                            updatePrize(
                                              idx,
                                              "imagePosition",
                                              pos
                                            );
                                            setCropModal(null);
                                          },
                                        });
                                      }}
                                    >
                                      <img
                                        src={prize.imagePreview}
                                        alt="Prize"
                                        style={{
                                          objectPosition: `${prize.imagePosition?.x ?? 50}% ${prize.imagePosition?.y ?? 50}%`,
                                        }}
                                        draggable={false}
                                      />
                                      <div className="incentive-preview-edit-hint">
                                        ✎ Adjust Crop
                                      </div>
                                      <ActionIcon
                                        className="incentive-prize-image-remove"
                                        variant="filled"
                                        color="red"
                                        size="xs"
                                        radius="xl"
                                        onClick={e => {
                                          e.stopPropagation();
                                          updatePrize(
                                            index,
                                            "imagePreview",
                                            null
                                          );
                                        }}
                                      >
                                        <IconX size={10} />
                                      </ActionIcon>
                                    </div>
                                  ) : (
                                    <div
                                      className="incentive-prize-image-upload"
                                      onClick={() => {
                                        setActivePrizeImageIndex(index);
                                        prizeImageInputRef.current?.click();
                                      }}
                                    >
                                      <IconUpload size={20} />
                                      <span>Photo</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </Stack>
                        {prizes.length < 16 && (
                          <Button
                            variant="light"
                            color="orange"
                            leftSection={<IconPlus size={14} />}
                            onClick={() => {
                              setPrizes(prev => [
                                ...prev,
                                {
                                  title: "",
                                  imagePreview: null,
                                  benchmark_value: String(prev.length + 1),
                                  image_url: "",
                                  prize_type: "place",
                                  prize_round: null,
                                },
                              ]);
                            }}
                            style={{ display: "flex", margin: "12px auto 0" }}
                          >
                            Add Prize
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* end tasks-list */}

              {/* ── Sticky Footer Actions ── */}
              <div
                style={{
                  padding: "16px 24px 24px",
                  borderTop: "1px solid #f3f4f6",
                  flexShrink: 0,
                }}
              >
                <Stack gap="sm">
                  {(errors.startDate || errors.endDate || errors.type) && (
                    <div
                      style={{
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        borderRadius: 8,
                        padding: "8px 12px",
                        fontSize: "0.8rem",
                        color: "#dc2626",
                      }}
                    >
                      {errors.type && <div>⚠ {errors.type}</div>}
                      {errors.startDate && (
                        <div>
                          ⚠ Start date is required — click{" "}
                          <strong>Start Date</strong> in the Configuration panel
                          on the left
                        </div>
                      )}
                      {errors.endDate && (
                        <div>
                          ⚠ End date is required — click{" "}
                          <strong>End Date</strong> in the Configuration panel
                          on the left
                        </div>
                      )}
                    </div>
                  )}
                  <Group justify="space-between">
                    <Group gap="xs">
                      <Button
                        variant="subtle"
                        color="gray"
                        onClick={() => navigate("/admin?tab=incentives")}
                        disabled={saving}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="light"
                        color="orange"
                        leftSection={<IconDeviceFloppy size={16} />}
                        onClick={() => handleSubmit(true)}
                        loading={saving}
                        disabled={!title.trim()}
                      >
                        Save Draft
                      </Button>
                    </Group>
                    <Group gap="xs">
                      {step > 0 && (
                        <Button
                          variant="default"
                          leftSection={<IconArrowLeft size={16} />}
                          onClick={() => setStep(s => s - 1)}
                          disabled={saving}
                        >
                          Back
                        </Button>
                      )}
                      {step < lastStep ? (
                        <Button
                          rightSection={<IconArrowRight size={16} />}
                          onClick={() => setStep(s => s + 1)}
                          disabled={!canNext()}
                          style={{ background: "#ff6b35", border: "none" }}
                        >
                          Next
                        </Button>
                      ) : (
                        <Button
                          leftSection={<IconCheck size={16} />}
                          onClick={() => handleSubmit(false)}
                          loading={saving}
                          style={{
                            background: "#ff6b35",
                            border: "none",
                            height: 48,
                            fontSize: "1rem",
                            fontWeight: 600,
                          }}
                        >
                          {isEditMode ? "Save Changes" : "Create Incentive"}
                        </Button>
                      )}
                    </Group>
                  </Group>
                </Stack>
              </div>
            </div>
            {/* end program-content */}
          </div>
          {/* end program-details-layout */}
        </div>
        {/* end program-details-container */}
      </PageContent>

      {/* Full-screen bracket preview modal */}
      {bracketPreviewModalOpen &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 10000,
              background: "rgba(0,0,0,0.85)",
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
            }}
            onClick={() => setBracketPreviewModalOpen(false)}
          >
            {/* Header bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "14px 24px",
                background: "white",
                borderBottom: "1px solid #e5e7eb",
                flexShrink: 0,
              }}
              onClick={e => e.stopPropagation()}
            >
              <div>
                <Text fw={700} size="md" style={{ color: "#1a1a1a" }}>
                  🏆 {title || "Bracket"} — Preview
                </Text>
                <Text size="xs" c="dimmed">
                  {new Date().toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </Text>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  onClick={() => setBracketPreviewModalOpen(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 36,
                    height: 36,
                    border: "1.5px solid #e5e7eb",
                    borderRadius: 8,
                    background: "white",
                    cursor: "pointer",
                    fontSize: "1.1rem",
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
            {/* Bracket area — scales to fit */}
            {(() => {
              const MATCHUP_W = 160;
              const BK_COL_GAP_PX = 56;
              const BK_SLOT_H_PX = 28;
              const BK_TITLE_H_PX = 14;
              const BK_MATCHUP_H_PX = BK_TITLE_H_PX + BK_SLOT_H_PX * 2;
              const r0Count = bracketRounds[0]?.matchupCount || 0;
              const naturalW =
                bracketRounds.reduce((w, _, idx) => {
                  return (
                    w +
                    MATCHUP_W +
                    (idx < bracketRounds.length - 1 ? BK_COL_GAP_PX : 0)
                  );
                }, 0) +
                BK_COL_GAP_PX +
                130 +
                80; // extra padding
              const naturalH = r0Count * BK_MATCHUP_H_PX + 80;
              const availW = window.innerWidth - 128;
              const availH = window.innerHeight - 160;
              const scaleW = availW / naturalW;
              const scaleH = availH / naturalH;
              const scale = Math.min(scaleW, scaleH, 1);
              const scaledW = naturalW * scale;
              const scaledH = naturalH * scale;
              return (
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 32,
                  }}
                  onClick={e => e.stopPropagation()}
                >
                  <div
                    style={{
                      background: "white",
                      borderRadius: 16,
                      width: scaledW,
                      height: scaledH,
                      overflow: "hidden",
                      position: "relative",
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        transformOrigin: "top left",
                        transform: `scale(${scale})`,
                        width: naturalW,
                        height: naturalH,
                        position: "absolute",
                        top: 0,
                        left: 0,
                        padding: 24,
                      }}
                    >
                      <BracketPreview
                        bracketRounds={bracketRounds}
                        bracketDesignSlots={bracketDesignSlots}
                        bracketParticipantNameMap={bracketParticipantNameMap}
                        displayedWinnersMatchNumbers={
                          displayedWinnersMatchNumbers
                        }
                        competitionType={competitionType}
                        losersBracketRounds={losersBracketRounds}
                        totalDisplayedWinnersMatches={
                          totalDisplayedWinnersMatches
                        }
                      />
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>,
          document.body
        )}

      {/* Portal dropdown for matchup rep search */}
      {matchupRepSearch &&
        dropdownCoords &&
        matchupRepSearchResults.length > 0 &&
        ReactDOM.createPortal(
          <div
            className="incentive-search-results"
            style={{
              position: "fixed",
              top: dropdownCoords.top,
              left: dropdownCoords.left,
              width: dropdownCoords.width,
              zIndex: 9999,
              borderTop: "1px solid #e5e7eb",
              borderRadius: "0 0 8px 8px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            }}
          >
            {matchupRepSearchResults.map(rep => (
              <div
                key={rep.user_season_id || rep.id}
                className="incentive-search-result"
                onMouseDown={e => e.preventDefault()}
                onClick={() =>
                  selectMatchupRep(
                    matchupRepSearch.matchupId,
                    matchupRepSearch.side,
                    rep.user_season_id || rep.id
                  )
                }
              >
                <IconUser size={15} color="#868e96" />
                <div style={{ flex: 1 }}>
                  <Text size="sm" fw={500}>
                    {rep.first_name} {rep.last_name}
                  </Text>
                  {rep.email && (
                    <Text size="xs" c="dimmed">
                      {rep.email}
                    </Text>
                  )}
                </div>
                <IconPlus size={14} color="#ff6b35" />
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}

export default IncentiveFormPage;
